/* =============================================================================
 *  TerminalEmailFeed
 *
 *  A terminal-native replacement for `EmailFeedContent`. Same data surface
 *  (`useEmailPanel()`), same actions — but rendered as a dense monospace list
 *  with bracketed-text controls instead of Metronic cards.
 *
 *  Used by `EmailSlidingPanel` when the active theme is `terminal`. The
 *  classic card-based feed keeps serving the default theme untouched.
 *
 *  Scope for v1 — matches what the user reaches for 99% of the time:
 *   - Browse inbox / sent / archive (Outlook-filed mail) / deleted / drafts / junk
 *   • Click row → open email
 *   - Local instant search, with operators (from: to: subject: has: is: in:
 *     before: after: and -negation; see ../utils/searchOperators) + deep search
 *   - Archive and delete as real Outlook moves with an [undo] toast; restore
 *     from archive / deleted; delete forever from Deleted
 *   - Bulk select (checkbox column on desktop, select mode on phones) with one
 *     action bar, one summary toast and one undo per batch
 *   • Mark all as read
 *   • Compose
 *   • Custom folder nav (read-only — no sender-rule editing here; use the
 *     classic feed for admin, or add a settings route later)
 *   • Load older
 *
 *  Out of scope for v1 (reachable via the classic feed): drag-to-folder,
 *  saved searches.
 * ============================================================================= */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useEmailPanel, PanelEmailData } from '../contexts/EmailPanelContext';
import { useAppData } from '../../../providers/AppDataProvider';
import type { FeedEmailData } from './EmailFeedContent';
import { markEmailRead, setEmailReadState, applyEmailReadState, readFieldsOf, isForbiddenError, isGraphWriteForbiddenNow, OWNER_ONLY_REASON } from '../utils/markEmailRead';
import { isDateOverdue } from '../../../utils/dateUtils';
import { warmGraphEmailBodyCache } from '../utils/graphEmailBodyCache';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { authFetch, authFetchJson, authPostConfirmed } from '../../../utils/cloudFunctionsAuth';
import { getGraphFlagWrite } from '../utils/emailFlagState';
import { toast } from 'react-toastify';
import TerminalNewJobModal from '../../../themes/terminal/TerminalNewJobModal';
import { useGraphiteTheme } from '../../../hooks/useGraphiteTheme';
import { useIsNarrow } from '../../../hooks/useIsNarrow';
import { useMailSyncHealth } from '../hooks/useMailSyncHealth';
import { MailSyncHealthPill, MailSyncHealthStrip, MailSyncHealthSheet } from './MailSyncHealth';
import { healthLevel, healthyTitle, summarizeSyncResults, syncMenuLabel } from '../utils/mailSyncModel';
import { normalizeEmailFolder, emailFolderTag, isOutlookFiled } from '../utils/emailFolder';
import type { EmailFolder } from '../utils/emailFolder';
import { resolveVirtualFolderId, matchVirtualFolderByOutlookName } from '../utils/virtualFolderMatch';
import { deleteEmailForever } from '../utils/mailActions';
import type { MoveDestination } from '../utils/mailActions';
import { showMoveUndoToast } from '../utils/undoToast';
import {
  addIds,
  alreadyAt,
  applyPendingMoves,
  isMovableEmail,
  joinReasons,
  locationSignature,
  mapLimit,
  moveSummary,
  nextCursorKey,
  planMoves,
  pruneSelection,
  rangeKeys,
  runMoves,
  selectionState,
  sweepPending,
  toMoveSubject,
  toggleIds,
  touchInflight,
  undoFailureText,
  undoMoves,
  undoableMoves,
} from '../utils/bulkMoves';
import type { MoveRow, PendingMap, PendingMove, RunMovesResult } from '../utils/bulkMoves';
import {
  SEARCH_OPERATOR_HELP,
  appendToQuery,
  highlightRegex,
  matchesSearch,
  parseSearchQuery,
  toServerQuery,
  usesOperators,
} from '../utils/searchOperators';
import type { SearchContext } from '../utils/searchOperators';
import BulkActionBar from './BulkActionBar';
import './TerminalEmailFeed.scss';

// Active job statuses — only these surface in the auto-suggest list and
// the manual search results, matching the default theme's behaviour.
const ACTIVE_JOB_STATUSES = ['Open', 'Active', 'Pending', 'Awaiting Client Response'];

// Folder semantics (inbox/sent/drafts/junk/deleted + archive/filed) live in
// ../utils/emailFolder - shared with the classic feed so an email's folder is
// identical in both views. Never re-derive it from `email.folder` here: a
// `deleted:true` + `folder:'inbox'` doc is mail that was MOVED out of the inbox
// and must not render as an inbox row.

/** Worth prefetching a body for? Only Graph rows whose full body isn't already
 *  on the doc (the feed snippet/text is short) and that carry enough Graph
 *  metadata to actually fetch. Pure — kept at module scope so the prefetch
 *  callbacks can be stable (empty-dep useCallback) without lint churn. */
function canPrefetchBody(email: FeedEmailData): boolean {
  if (email.source !== 'graph') return false;
  if (!email.mailbox || !email.graph?.id) return false;
  const hasFullBody = !!((email.html && email.html.length > 200) || (email.text && email.text.length > 300));
  return !hasFullBody;
}

function isUnread(email: FeedEmailData): boolean {
  if (email.source === 'graph') {
    const v = (email as any).isRead;
    if (typeof v === 'boolean') return v === false;
    return !(email as any).dismissed;
  }
  return !(email as any).dismissed;
}

// Compact date for the row prefix. Keeps the standard `DD/MM` shape for
// emails inside the last 12 months — the common case — and falls back to
// `DD/MM/YY` for anything older so a 2-year-old search hit can't be
// confused with a recent one. The 12-month cutoff is sliding (not the
// calendar year) so a 14-month-old email always shows its year, even in
// January when the calendar year just rolled.
function formatShortDate(d: Date): string {
  const day = ('0' + d.getDate()).slice(-2);
  const month = ('0' + (d.getMonth() + 1)).slice(-2);
  const ageMs = Date.now() - d.getTime();
  const TWELVE_MONTHS_MS = 365 * 24 * 60 * 60 * 1000;
  if (ageMs > TWELVE_MONTHS_MS) {
    const yy = ('0' + (d.getFullYear() % 100)).slice(-2);
    return `${day}/${month}/${yy}`;
  }
  return `${day}/${month}`;
}

function senderDisplay(email: FeedEmailData): string {
  const first = email.from?.[0];
  if (first?.name && first.name.trim()) return first.name.trim();
  if (first?.address) return first.address;
  if (email.sender) return email.sender;
  return '—';
}

/** Recipient display for sent/drafts. Pulls the first 'to' entry; falls back
 *  to a count if there are multiple addresses. */
function recipientDisplay(email: FeedEmailData): string {
  const to = email.to;
  const list: string[] = Array.isArray(to)
    ? to.filter(Boolean) as string[]
    : (typeof to === 'string' && to.trim() ? [to] : []);
  if (list.length === 0) return '—';
  // Strip "Name <addr@x>" if present and prefer the name.
  const first = list[0].trim();
  const m = first.match(/^"?([^"<]+?)"?\s*<([^>]+)>$/);
  const display = m ? m[1].trim() : first;
  return list.length > 1 ? `${display} +${list.length - 1}` : display;
}

/** Did the mailbox owner write this mail? Sent/drafts are outbound by folder,
 *  but the sync also stores a Sent-Items copy that was filed into Outlook's
 *  Archive / another folder (or found by search) as emailType:'sent' - there
 *  the folder says "filed" and only emailType carries the direction. Reading
 *  the folder alone shows the owner's own name as the "sender" of their reply
 *  and (in the waiting ledger) mistakes it for the customer answering. */
function isOutboundMail(email: FeedEmailData, folder: EmailFolder): boolean {
  return folder === 'sent'
    || folder === 'drafts'
    // A sent message the owner later deleted sits in Deleted Items, also with
    // emailType:'sent': it is still "to: them", not "from: me".
    || ((isOutlookFiled(folder) || folder === 'deleted') && email.emailType === 'sent');
}

/** Folder-aware contact display: shows recipient for outbound, sender for inbound. */
function contactDisplay(email: FeedEmailData, folder: EmailFolder): string {
  if (isOutboundMail(email, folder)) return recipientDisplay(email);
  return senderDisplay(email);
}

/** First line of the plain-text body, single-line, trimmed.
 *  Strips the common "Sent from my..." footers + quoted blocks for cleaner
 *  previews. */
function snippetText(email: FeedEmailData): string {
  const raw = String((email as any).message || (email as any).text || '').trim();
  if (!raw) return '';
  // Strip the most common quote prefixes / signature noise so the first
  // visible line is usually the actual message.
  const cleaned = raw
    .replace(/^>+.*$/gm, '')                    // quoted lines
    .replace(/^On .{0,80}wrote:.*/im, '')      // reply quote header
    .replace(/Sent from my .+$/im, '')         // mobile footer
    .replace(/\r?\n+/g, ' ')                    // collapse newlines
    .replace(/\s{2,}/g, ' ')                    // collapse whitespace
    .trim();
  return cleaned;
}

// One compiled regex per distinct term list: every visible row highlights up to three
// fields on every render, and the list rarely changes between them.
let highlightKey = '';
let highlightRe: RegExp | null = null;

/** Inline search-term highlighter - wraps FREE-TEXT matches in a span we colour via CSS
 *  (operator names like `from:` are never passed in, so they are never highlighted). */
function highlight(text: string, terms: ReadonlyArray<string>): React.ReactNode {
  if (!text || terms.length === 0) return text;
  const key = terms.join('\u0000');
  if (key !== highlightKey) { highlightKey = key; highlightRe = highlightRegex(terms); }
  if (!highlightRe) return text;
  // The regex has ONE capture group, so split() puts every match at an odd index.
  const parts = text.split(highlightRe);
  if (parts.length <= 1) return text;
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1
          ? <span key={i} className="term-mail__highlight">{part}</span>
          : <React.Fragment key={i}>{part}</React.Fragment>
      )}
    </>
  );
}

// (Read/unread has no URL here: it goes through setEmailReadState in
// ../utils/markEmailRead so every surface shares one verified path.)
const SET_GRAPH_FLAG_URL =
  'https://us-central1-web-construct-app.cloudfunctions.net/setGraphEmailFlagState';
const TOGGLE_GRAPH_JUNK_URL =
  'https://us-central1-web-construct-app.cloudfunctions.net/toggleGraphJunk';
// (Archive / delete / restore / delete-forever go through ../utils/mailActions.)
const CHECK_NEW_EMAILS_URL =
  'https://us-central1-web-construct-app.cloudfunctions.net/checkNewEmails';
const OUTBOX_URL =
  'https://us-central1-web-construct-app.cloudfunctions.net/manageEmailOutbox';

// A stand-in React.MouseEvent for the per-row handlers when they're invoked
// from the keyboard (they only ever call e.stopPropagation()).
const noopMouseEvt = () => ({ stopPropagation() {}, preventDefault() {} } as unknown as React.MouseEvent);

// Stable identity of a feed row — the key the keyboard cursor anchors to.
const rowKeyOf = (e: FeedEmailData): string => String(e.emailId || e.id || '');

// Shared toast ids: a burst of failing / skipped moves (a held key, a whole bulk batch)
// shows ONE toast, not a stack.
const MOVE_FAILED_TOAST_ID = 'mail-move-failed';
const MOVE_NOTE_TOAST_ID = 'mail-move-note';
const UNDO_DETAIL_TOAST_ID = 'mail-undo-detail';
// Moves in flight at once for a thread / bulk batch.
const MOVE_CONCURRENCY = 4;

// Buttons / checkboxes of the selection + help UI. Enter on one of them activates THAT control;
// the feed's own Enter (open the cursor row, which also marks it read) must not fire as well.
const OWN_ENTER_CONTROLS =
  '.term-bulk, .term-mail__help, .term-mail__help-btn, .term-mail__select-btn, .term-mail__statusbar__btn, .term-mail__row__thread-badge, .term-mail__row__check';

// Shown when a search has operators but nothing the server can search (see localOnlySearch).
const LOCAL_ONLY_SEARCH_HINT =
  'Nothing here for the full-mailbox search (it needs a word, from:, to: or subject:), so this only '
  + 'filters the mail already loaded: recent mail plus anything opened or searched earlier. Add a word '
  + 'or from:/to:/subject: to search the whole mailbox.';

// Human-readable reason out of a thrown value (HttpError carries the server's
// own error text; Firestore errors carry theirs). A 403 is the owner-only gate
// on every Graph write / manual sync endpoint: say that, not a bare "Forbidden".
const errText = (err: unknown): string => {
  if (isForbiddenError(err)) return OWNER_ONLY_REASON;
  const m = (err as any)?.message;
  return typeof m === 'string' && m.trim() ? m.trim() : 'unknown error';
};

// ── Snooze presets ──────────────────────────────────────────────────────────
// Snooze hides an email from the inbox until a wake time, then it resurfaces.
// Purely client-side: we stamp `snoozedUntil` (a Timestamp) and filter on it.
function snoozePresets(): Array<{ label: string; ms: number }> {
  const now = new Date();
  const laterToday = new Date(now); laterToday.setHours(now.getHours() + 3, 0, 0, 0);
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1); tomorrow.setHours(8, 0, 0, 0);
  const nextWeek = new Date(now); nextWeek.setDate(now.getDate() + 7); nextWeek.setHours(8, 0, 0, 0);
  return [
    { label: 'Later today (+3h)', ms: laterToday.getTime() },
    { label: 'Tomorrow 8am',      ms: tomorrow.getTime() },
    { label: 'Next week',         ms: nextWeek.getTime() },
  ];
}
function tomorrowMorningMs(): number {
  const t = new Date(); t.setDate(t.getDate() + 1); t.setHours(8, 0, 0, 0);
  return t.getTime();
}
function formatSnoozeLabel(ms: number): string {
  const d = new Date(ms);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((new Date(d).setHours(0, 0, 0, 0) - today.getTime()) / 86400000);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (dayDiff <= 0) return `today ${time}`;
  if (dayDiff === 1) return `tomorrow ${time}`;
  if (dayDiff < 7) return `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// Compact money for the overdue chip: $450, $1.2k, $12k.
function shortMoney(n: number): string {
  const v = Math.abs(n);
  if (v >= 1000) return `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(v)}`;
}

function formatTimeAgo(d: Date | null): string {
  if (!d) return '';
  const diff = Math.max(0, Date.now() - d.getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const TerminalEmailFeed: React.FC = () => {
  const {
    preLoadedEmails,
    isLoadingInitialEmails,
    openEmailPanel,
    closeEmailPanel,
    setEmailPanelView,
    setDraftMode,
    customFolders,
    userDrafts,
    isLoadingOlderEmails,
    hasMoreOlderEmails,
    loadOlderEmails,
    loadEmailsByIds,
    // Folder + sender management surface (already backed by Firestore in
    // EmailPanelContext — the classic feed uses these too).
    createCustomFolder,
    renameCustomFolder,
    deleteCustomFolder,
    addSenderToFolder,
    removeSenderFromFolder,
    liveInboxIds,
  } = useEmailPanel();

  // Customer + job lookup — cheap reads from AppDataProvider (already
  // app-wide). Used for both the search-customer match in the search
  // predicate and the Link to Job menu section.
  const { customers, jobs: allJobs, invoices } = useAppData();

  // A coarse clock (updates every 60s) so time-based views — snooze wake-ups,
  // overdue rollovers — recompute even when no Firestore snapshot arrives.
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 60000);
    return () => window.clearInterval(id);
  }, []);
  const customerNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of customers) {
      if (c.id) map.set(c.id, c.name || c.company || c.email || 'unknown');
    }
    return map;
  }, [customers]);
  // Lowercased email → customerId map for sender → customer matching.
  const customerIdByEmail = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of customers) {
      const e = (c.email || '').toLowerCase().trim();
      if (e && c.id) map.set(e, c.id);
    }
    return map;
  }, [customers]);
  const activeJobs = useMemo(
    () => allJobs.filter((j) => ACTIVE_JOB_STATUSES.includes(j.status)),
    [allJobs],
  );

  // Overdue money owed per customer — drives the red "overdue" row chip so the
  // owner sees, right on the feed, that the person emailing them owes money.
  // Only past-due, still-owing invoices count (PAID/VOIDED/DRAFT excluded).
  const overdueByCustomer = useMemo(() => {
    // NOTE: useAppData().invoices are Xero-shaped (PascalCase) objects —
    // Status / AmountDue / DueDateString / Contact.ContactID — NOT the
    // camelCase accounting Invoice. Overdue is a calendar-date test in Adelaide
    // (isDateOverdue), matching every other overdue surface in the app.
    // nowTick is a dep so the set refreshes at the day rollover.
    void nowTick;
    const map = new Map<string, { count: number; amount: number }>();
    for (const inv of (invoices || [])) {
      const status = String((inv as any).Status || '').toUpperCase();
      if (status === 'PAID' || status === 'VOIDED' || status === 'DELETED' || status === 'DRAFT') continue;
      const due = Number((inv as any).AmountDue || 0);
      if (due <= 0) continue;
      const dueStr = (inv as any).DueDateString || (inv as any).DueDate;
      if (!isDateOverdue(dueStr)) continue; // only PAST-due (Adelaide calendar)
      const cid = (inv as any).Contact?.ContactID;
      if (!cid) continue;
      const cur = map.get(cid) || { count: 0, amount: 0 };
      cur.count += 1; cur.amount += due;
      map.set(cid, cur);
    }
    return map;
  }, [invoices, nowTick]);

  // Resolve the "true" customer for an email — recipient on outbound,
  // sender on inbound, or the explicit customerId field if set.
  const getTrueCustomerId = useCallback((email: FeedEmailData): string | null => {
    const isOutbound = email.folder === 'sent' || email.emailType === 'sent';
    if (isOutbound) {
      const recipientList: string[] = Array.isArray(email.to)
        ? (email.to as string[])
        : (typeof email.to === 'string' ? [email.to] : []);
      for (const raw of recipientList) {
        const m = String(raw).match(/<([^>]+)>$/);
        const addr = (m ? m[1] : String(raw)).toLowerCase().trim();
        const matched = customerIdByEmail.get(addr);
        if (matched) return matched;
      }
      return null;
    }
    if (email.customerId && typeof email.customerId === 'string') return email.customerId;
    const senderAddr = email.from?.[0]?.address?.toLowerCase().trim();
    if (senderAddr) {
      const matched = customerIdByEmail.get(senderAddr);
      if (matched) return matched;
    }
    return null;
  }, [customerIdByEmail]);

  const getJobsForCustomer = useCallback((customerId: string) =>
    activeJobs.filter((j) => {
      const cid = (j as any).customerId;
      return Array.isArray(cid) ? cid.includes(customerId) : cid === customerId;
    }),
    [activeJobs],
  );

  // Search state lives in EmailPanelContext so it survives the feed
  // component unmounting (closing the panel, opening an email modal).
  // Local state would reset on every remount and the user would lose
  // their query.
  const {
    searchTerm,
    setSearchTerm,
    isDeepSearching,
    setIsDeepSearching,
    lastDeepSearchTerm,
    setLastDeepSearchTerm,
    deepSearchHits,
    setDeepSearchHits,
    deepSearchIds,
    setDeepSearchIds,
  } = useEmailPanel();
  const [activeFolder, setActiveFolder] = useState<string>('inbox'); // 'inbox' | 'sent' | 'archive' | 'drafts' | 'junk' | 'deleted' | `f_<id>` (custom)
  const [displayCount, setDisplayCount] = useState(50);
  // Graphite renders this terminal feed re-skinned warm; drop the [bracket] chrome.
  const isGraphite = useGraphiteTheme();
  const br = (s: string) => (isGraphite ? s : `[${s}]`);
  // Phones: topbar buttons shrink to their icon glyphs so search keeps room.
  const isNarrow = useIsNarrow();
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  // Phone topbar [⋯] sheet — holds mark-all-read / sync / threads so the
  // search row never wraps.
  const [topMenuOpen, setTopMenuOpen] = useState(false);
  // Mail sync health (which mailboxes are actually syncing) — drives the topbar
  // pill on desktop and the strip / sheet on phones. Details sheet open state
  // lives here because two triggers open it (the strip and the [⋯] sheet item).
  const { health: syncHealth, loading: syncHealthLoading, error: syncHealthError, refresh: refreshSyncHealth } = useMailSyncHealth();
  const [syncSheetOpen, setSyncSheetOpen] = useState(false);
  const closeSyncSheet = useCallback(() => setSyncSheetOpen(false), []);
  const syncHealthNotOk = healthLevel(syncHealth) !== 'ok';
  // A parked, known condition (only paused mailboxes): keep it reachable in the
  // [⋯] sheet but BELOW the real actions, so "Mark all as read" - the most used
  // item - never moves for a mailbox the owner already knows is dead.
  const syncHealthQuiet = healthLevel(syncHealth) === 'info';
  const syncMenuItem = syncHealthNotOk ? (
    <button
      type="button"
      className="term-mail__row__menu-item"
      onClick={() => { setTopMenuOpen(false); setSyncSheetOpen(true); }}
    >
      {syncMenuLabel(syncHealth)}
    </button>
  ) : null;
  useEffect(() => {
    if (!topMenuOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('.term-mail__topmenu')) return;
      setTopMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [topMenuOpen]);

  // ── Keyboard triage cursor ────────────────────────────────────────────
  // A Gmail/mutt-style cursor over the rendered list: j/k move, Enter opens,
  // e marks read, u toggles unread, p pins, a archives, r restores to the inbox,
  // # deletes (-> Deleted Items, undoable; inside Deleted: forever), x ticks the
  // row for a bulk action, s snoozes, / focuses search, ? shows the search
  // operators, c composes, Esc backs out one layer (help / selection / cursor).
  // (`e` stays "mark read" - it was the feed's triage key before archive existed.)
  // With rows ticked, a / r / # act on the selection instead of the cursor row.
  //
  // Anchored by EMAIL ID (not a bare position): the live inbox prepends new
  // mail and pin/snooze reorders rows, so a positional index would silently
  // retarget — pressing `#` could then delete the wrong message. cursorId is
  // the source of truth; cursorIdx is derived from it each render.
  const [cursorId, setCursorId] = useState<string | null>(null);
  const lastCursorIdxRef = useRef<number>(-1);
  const listRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // ── Folder CRUD UI state ──────────────────────────────────────────────
  // "create" and "rename" both swap a sidebar row for an <input>. Only one
  // can be active at a time; entering rename closes create and vice versa.
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [renamingFolderDraft, setRenamingFolderDraft] = useState('');

  // ── Per-row action pending dedupe ─────────────────────────────────────
  // Maps emailId → set of in-flight action names. Guards against rapid
  // double-clicks on pin/junk/read/delete turning into ping-pong writes.
  const [pendingByEmailId, setPendingByEmailId] = useState<Record<string, Record<string, boolean>>>({});
  // One row's [⋮] menu open at a time. Mirrors the default theme's
  // dropdown — single trigger per row, full action surface inside.
  const [actionMenuFor, setActionMenuFor] = useState<string | null>(null);

  // -- Archive / delete / restore: the optimistic overlay ----------------
  // id -> what is happening to that email (bulkMoves.PendingMove). A row whose
  // move is in flight is hidden AND out of every count at once; when the server
  // confirms, it shows at its new location until the Firestore snapshot catches
  // up. Entries never linger: they end on the snapshot, on a failure, on undo,
  // or after 30s (the sweep effect below).
  const [pendingMoves, setPendingMoves] = useState<PendingMap>({});
  // Async handlers read the latest map through this ref (their closures are old).
  const pendingMovesRef = useRef<PendingMap>(pendingMoves);
  pendingMovesRef.current = pendingMoves;
  // Progress line of a running bulk batch ("moving 7/12..."); locks the bulk bar.
  const [bulkBusy, setBulkBusy] = useState<string | null>(null);

  // -- Bulk selection (by email id) --------------------------------------
  // selectMode is the explicit "tap rows to select" mode (phones); on desktop
  // ticking a checkbox is enough. The anchor is the last-toggled row, for shift-click ranges.
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [selectMode, setSelectMode] = useState(false);
  const selectAnchorRef = useRef<string | null>(null);
  const selecting = selectMode || selectedIds.size > 0;

  // -- Search help ('?') + phone keyboard state --------------------------
  const [helpOpen, setHelpOpen] = useState(false);
  // While the phone keyboard is up the bulk bar steps aside (it would sit behind it).
  const [searchFocused, setSearchFocused] = useState(false);

  // ── Link-to-Job state ─────────────────────────────────────────────────
  // Mirrors the default theme. Three flows:
  //   1. Direct link from menu: handleLinkToJob(emailId, jobId, jobLabel)
  //   2. Search-customer modal: pick a customer → pick a job → link
  //   3. Create-new-job modal: pre-fills customer + email → link on create
  const [linkingEmailId, setLinkingEmailId] = useState<string | null>(null);
  const [linkModalOpen, setLinkModalOpen]   = useState(false);
  const [linkModalEmail, setLinkModalEmail] = useState<FeedEmailData | null>(null);
  const [linkModalSelectedCustomerId, setLinkModalSelectedCustomerId] = useState<string | null>(null);
  const [linkModalSearch, setLinkModalSearch] = useState('');
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [newJobEmailContext, setNewJobEmailContext] = useState<{
    email: FeedEmailData;
    customerId: string | null;
  } | null>(null);

  // ── Outbox (send-later) state ─────────────────────────────────────────
  // Scheduled sends live in email_outbox (server-side), not the emails
  // collection — fetched on entering the outbox view, not subscribed.
  const [outboxItems, setOutboxItems] = useState<any[]>([]);
  const [outboxLoading, setOutboxLoading] = useState(false);
  const [outboxCancelling, setOutboxCancelling] = useState<string | null>(null);

  const loadOutbox = useCallback(async () => {
    setOutboxLoading(true);
    try {
      const resp = await authFetch(OUTBOX_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list' }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data?.ok) setOutboxItems(Array.isArray(data.items) ? data.items : []);
      else toast.error(`outbox load failed: ${data?.error || `HTTP ${resp.status}`}`);
    } catch (err: any) {
      toast.error(`outbox load failed: ${err?.message || 'network error'}`);
    } finally {
      setOutboxLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeFolder === 'outbox') void loadOutbox();
  }, [activeFolder, loadOutbox]);

  // Load once on mount too, so the sidebar badge is truthful before the view
  // is ever visited.
  useEffect(() => { void loadOutbox(); }, [loadOutbox]);

  // Searching from the outbox view: the outbox render path ignores search, so
  // silently showing "matches N" chrome over an unfiltered outbox would lie.
  // Jump to the normal list (search spans all folders there anyway).
  useEffect(() => {
    if (activeFolder === 'outbox' && searchTerm.trim()) setActiveFolder('inbox');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, activeFolder]);

  const cancelOutboxItem = useCallback(async (id: string) => {
    if (outboxCancelling) return;
    setOutboxCancelling(id);
    try {
      const resp = await authFetch(OUTBOX_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'cancel', id }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data?.ok) {
        toast.success('cancelled');
        setOutboxItems((prev) => prev.filter((x) => x.id !== id));
      } else {
        toast.error(`cancel failed: ${data?.error || `HTTP ${resp.status}`}`);
        void loadOutbox(); // re-sync — it may have just fired
      }
    } catch (err: any) {
      toast.error(`cancel failed: ${err?.message || 'network error'}`);
    } finally {
      setOutboxCancelling(null);
    }
  }, [outboxCancelling, loadOutbox]);

  // ── Manual sync state ─────────────────────────────────────────────────
  const [isSyncingMailbox, setIsSyncingMailbox] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  // info = neutral/ok, warn = amber (some mailboxes not checked), error = red.
  const [syncTone, setSyncTone] = useState<'info' | 'warn' | 'error'>('info');
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const syncStatusClearTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (syncStatusClearTimerRef.current) window.clearTimeout(syncStatusClearTimerRef.current);
  }, []);
  // Single writer for the status strip. `clearAfterMs: null` = stay until the
  // next sync or the user dismisses it — failures and partial syncs must NOT
  // fade on a timer, or the one message that says "something is wrong" is gone
  // before it is read.
  const flashStatus = useCallback((
    text: string | null,
    tone: 'info' | 'warn' | 'error' = 'info',
    clearAfterMs: number | null = null,
  ) => {
    if (syncStatusClearTimerRef.current) {
      window.clearTimeout(syncStatusClearTimerRef.current);
      syncStatusClearTimerRef.current = null;
    }
    setSyncStatus(text);
    setSyncTone(tone);
    if (text && clearAfterMs) {
      syncStatusClearTimerRef.current = window.setTimeout(() => setSyncStatus(null), clearAfterMs);
    }
  }, []);

  // Deep search (Microsoft Graph $search) — see context block above for
  // the lifted state. Only the abort guard is local because cancelling
  // an in-flight call is a per-mount concern; if the feed remounts
  // mid-search, the previous request is allowed to settle and write
  // its result into the persistent context state.
  const deepSearchAbortRef = useRef<{ aborted: boolean } | null>(null);

  // Single-source email search via Microsoft Graph $search.
  //
  // Graph is the canonical source for every email — same index Outlook
  // uses to return hundreds of hits in seconds across years of mail.
  // We mirror Outlook's architecture: ask Graph, render what comes back.
  // No Algolia leg, no second-source merge, no "what does our cached
  // copy say" tiebreaker.
  //
  // Algolia is still alive in the codebase but only feeds the AI
  // assistant's `searchCustomerCommunication` tool (chat history +
  // learned notes). It deliberately does NOT touch email search; the
  // cost was duplicate-source flakiness that made repeat searches
  // return different results.
  //
  // Best-effort: a failed Graph search falls through to the local
  // tokenized filter against preLoadedEmails so the feed never blanks.
  const runDeepSearch = useCallback(async (term: string) => {
    const q = term.trim();
    if (q.length < 2) return;
    // Cancel any in-flight earlier search so we don't race results.
    if (deepSearchAbortRef.current) deepSearchAbortRef.current.aborted = true;
    // What Graph can be asked: the query as typed, or - when it has operators - only the
    // parts the server reads as KQL (from: to: subject: has:attachment + the free text).
    // The rest (is: in: dates -negation) is applied locally to whatever comes back, see
    // `searched` below. Nothing for Graph to search (e.g. "is:unread") = local filtering only.
    const serverText = toServerQuery(parseSearchQuery(q), q).query;
    if (serverText.length < 2) {
      setIsDeepSearching(false);
      setDeepSearchHits(null);
      setDeepSearchIds(null);
      setLastDeepSearchTerm('');
      return;
    }
    const guard = { aborted: false };
    deepSearchAbortRef.current = guard;

    setIsDeepSearching(true);
    // Keyed by what was SENT, not what was typed: adding a local-only operator (is:unread on
    // "invoice") leaves the server text alone, so its hits stay and no second 5-30s search runs
    // (see the debounce effect below and `searched`). A plain query sends exactly what was typed.
    setLastDeepSearchTerm(serverText);
    setDeepSearchHits(null);
    setDeepSearchIds(null);

    try {
      const resp = await authFetch(
        'https://us-central1-web-construct-app.cloudfunctions.net/searchGraphMail',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: serverText,
            // Thorough sweep: 20 pages × 100/page, 2000-cap per mailbox.
            // Graph short-circuits once results are exhausted, so narrow
            // queries return quickly. The backend now upserts in parallel
            // with subsequent page fetches, so wall-clock time tracks
            // Graph's own latency rather than our post-processing.
            maxPages: 20,
            top: 100,
            limit: 2000,
            searchEntireMailbox: true,
          }),
        },
      );
      if (guard.aborted) return;

      let data: any = null;
      try { data = await resp.json(); } catch { /* ignore */ }
      if (!resp.ok || !data?.ok) {
        const msg = data?.error || `HTTP ${resp.status}`;
        console.warn('[TerminalEmailFeed] Graph search failed:', msg);
        if (guard.aborted) return;
        flashStatus(`search failed: ${msg}`, 'info', 3500);
        return;
      }

      const items: any[] = Array.isArray(data?.items) ? data.items : [];
      const ids: string[] = items.map((i: any) => i?.docId).filter(Boolean);
      if (guard.aborted) return;

      setDeepSearchHits(items.length);
      setDeepSearchIds(new Set(ids));
      if (ids.length > 0) {
        // Hydrate search hits into the feed. No special tagging needed:
        // the dashboard / badge gate on the liveInboxIds allowlist (owned
        // by the canonical subscription), so search results outside the
        // live window can't pollute "Waiting For You".
        try { await loadEmailsByIds(ids); }
        catch (err) { console.warn('[TerminalEmailFeed] loadEmailsByIds failed:', err); }
      } else {
        flashStatus(`no matches for "${q}"`, 'info', 3500);
      }
    } catch (err: any) {
      if (!guard.aborted) console.error('[TerminalEmailFeed] Graph search error:', err);
    } finally {
      if (!guard.aborted) setIsDeepSearching(false);
    }
  }, [loadEmailsByIds, flashStatus]);

  // Auto-trigger debounce. Fires whenever the search term changes and is
  // 2+ chars. Skips if we've already searched the exact same term in
  // this session — re-firing on every keystroke focus event would waste
  // a 5-30s server roundtrip for no new information.
  useEffect(() => {
    const q = searchTerm.trim();
    if (q.length < 2) {
      // Clear any in-flight search and reset state.
      if (deepSearchAbortRef.current) deepSearchAbortRef.current.aborted = true;
      setIsDeepSearching(false);
      setDeepSearchHits(null);
      setDeepSearchIds(null);
      setLastDeepSearchTerm('');
      return;
    }
    // If the server would be asked what we already asked it, don't re-fire: the re-typed term, and
    // also an edit that only touches local operators (is: in: dates -negation), which never reach
    // the server, so the hits we hold are still the right ones.
    const serverKey = toServerQuery(parseSearchQuery(q), q).query;
    if (serverKey.toLowerCase() === lastDeepSearchTerm.toLowerCase() && deepSearchHits !== null) return;
    const t = window.setTimeout(() => { runDeepSearch(q); }, 700);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Close the row [⋮] menu on any click outside of it. mousedown so we
  // run before other click handlers — a click on a non-menu element
  // shouldn't leave the menu lingering over the new context. touchstart
  // too: iOS never synthesizes mousedown for scroll gestures, which left
  // the menu dangling open while the list moved underneath it.
  useEffect(() => {
    if (!actionMenuFor) return;
    const onDocDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('.term-mail__row__menu-wrap')) return;
      setActionMenuFor(null);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
    };
  }, [actionMenuFor]);

  // Re-resolve virtualFolderId live so newly added sender rules apply without reload.
  // Inbox/junk mail is routed by sender rule; mail the owner filed into an
  // Outlook folder is routed FIRST by that folder's name (a virtual "Trinity"
  // folder mirrors the Outlook "Trinity" folder), then by sender rule.
  // (virtualFolderMatch.ts holds the pure rules + tests.)
  //
  // applyPendingMoves runs FIRST: a row being archived / deleted is hidden (and so out of the
  // list, the counts, the waiting ledger and the threads) the instant the action starts, and
  // shows at its confirmed new location until the snapshot catches up.
  const enriched = useMemo(() => {
    // Purged = deleted forever from Outlook's Deleted Items (the doc is kept server-side for
    // history, never listed here), so it must not reappear in the Deleted view after a reload.
    return applyPendingMoves(preLoadedEmails, pendingMoves).filter((email) => !email.purged).map((email) => ({
      ...email,
      virtualFolderId: resolveVirtualFolderId(email, customFolders),
    }));
  }, [preLoadedEmails, customFolders, pendingMoves]);

  // Retire finished overlay entries: the snapshot shows the email somewhere new, it is gone, or
  // it waited 30s (see bulkMoves.sweepPending for the rules, incl. search hits outside the live
  // window that never get a snapshot). A slow interval covers time passing with no snapshot.
  useEffect(() => {
    if (Object.keys(pendingMoves).length === 0) return undefined;
    const byId = new Map<string, MoveRow>();
    for (const e of preLoadedEmails) byId.set(e.id, e);
    const isLive = (id: string) => liveInboxIds.size === 0 || liveInboxIds.has(id);
    const sweep = () => setPendingMoves((prev) => sweepPending(prev, byId, Date.now(), isLive));
    sweep();
    const t = window.setInterval(sweep, 5000);
    return () => window.clearInterval(t);
  }, [pendingMoves, preLoadedEmails, liveInboxIds]);

  // Search filter. Always-additive UNION of two sources:
  //
  //   (a) Local tokenized AND-match across every searchable field on the
  //       row (subject / sender / snippet / recipient / customer name).
  //       Whitespace splits the query, every token must appear somewhere
  //       in the joined haystack.
  //
  //   (b) Server canonical match set — Graph $search + Algolia, merged
  //       into deepSearchIds as each source returns. Trusted as-is for
  //       the current search term; covers cases where local fields don't
  //       carry every token (Graph's full-body $search hits text the
  //       row's truncated `message` snippet doesn't expose).
  //
  // Why union (rather than "use canonical when available, fall back to
  // local"): the previous "switch modes" approach caused a flicker —
  // local match while waiting → row visible → server returns first batch
  // → mode switches to canonical → row disappears (it was a local match
  // not in the first server batch) → next server batch returns → row
  // reappears. Union is monotonically additive: once a row is shown,
  // it stays shown for the duration of this search term.
  //
  // Operators (from: to: subject: has: is: in: before: after: and -negation, see
  // ../utils/searchOperators) narrow BOTH legs. A plain query without any behaves exactly as it
  // always did. `in:` picks its folder here, whatever view is open - search already spans every
  // folder, so `in:deleted foo` works from the inbox view.
  const parsedSearch = useMemo(() => parseSearchQuery(searchTerm), [searchTerm]);
  const searchTerms = parsedSearch.terms; // highlighted in rows; operator names never are
  const serverQuery = useMemo(() => toServerQuery(parsedSearch, searchTerm), [parsedSearch, searchTerm]);
  // A query whose operators all stay local (is: in: before: after: -negation, with no word /
  // from: / to: / subject: for Graph to search) never reaches the server: it only filters the mail
  // already loaded. The search box promises the whole mailbox, so say so when that is not what ran.
  const localOnlySearch = searchTerm.trim().length >= 2 && usesOperators(parsedSearch) && serverQuery.query.length < 2;
  const virtualFolderNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of customFolders) if (f.id && f.name) map.set(f.id, f.name);
    return map;
  }, [customFolders]);

  const searched = useMemo(() => {
    const raw = searchTerm.trim().toLowerCase();
    if (!raw) return enriched;

    // The hits answer the SERVER query they were fetched for, so they stay while it is unchanged
    // (a local-only operator added or edited later filters the same hits instead of dropping them).
    const serverKey = serverQuery.query.toLowerCase();
    const canonicalSet = (deepSearchIds && serverKey && serverKey === lastDeepSearchTerm.toLowerCase())
      ? deepSearchIds
      : null;
    const withOperators = usesOperators(parsedSearch);

    return enriched.filter((e) => {
      const ctx: SearchContext = {
        folder: normalizeEmailFolder(e),
        unread: isUnread(e),
        customerName: e.customerId ? (customerNameById.get(String(e.customerId)) || '') : '',
        virtualFolderName: e.virtualFolderId ? virtualFolderNameById.get(e.virtualFolderId) : undefined,
      };
      // Server-canonical hit - included. The server already matched the words (and any
      // operators it understood); an operator query still has to satisfy what it could not
      // apply (is: in: dates -negation). A plain query trusts the hit outright, as before.
      const id = e.emailId || e.id;
      if (canonicalSet && id && canonicalSet.has(id)) {
        return withOperators
          ? matchesSearch(e, parsedSearch, ctx, { skipTerms: true, skipFilters: serverQuery.handled })
          : true;
      }

      // Otherwise local match: every term / operator must hold.
      return matchesSearch(e, parsedSearch, ctx);
    });
  }, [enriched, searchTerm, customerNameById, virtualFolderNameById, deepSearchIds, lastDeepSearchTerm, parsedSearch, serverQuery]);

  // ── Waiting-on-them ledger ─────────────────────────────────────────────
  // The client emails you're awaiting a reply to: per customer, the most recent
  // OUTBOUND email that (a) is newer than any inbound from them and (b) is older
  // than a grace window (so a send from 10 minutes ago isn't nagging you). One
  // row per waiting customer — the last thing you sent them.
  const WAITING_GRACE_MS = 24 * 60 * 60 * 1000;
  const waitingEmailIds = useMemo(() => {
    const now = nowTick;
    const agg = new Map<string, { lastInbound: number; lastOutbound: FeedEmailData | null; lastOutboundTs: number }>();
    for (const e of enriched) {
      if (liveInboxIds.size > 0 && !liveInboxIds.has(e.id)) continue;
      const s = (e as any).snoozedUntil as number | null | undefined;
      if (s && s > now) continue; // snoozed → out of the ledger too
      const cid = getTrueCustomerId(e);
      if (!cid) continue;
      const f = normalizeEmailFolder(e);
      const ts = e.createdAt.getTime();
      const cur = agg.get(cid) || { lastInbound: 0, lastOutbound: null, lastOutboundTs: 0 };
      // A filed Sent-Items copy (emailType:'sent') is still something the owner
      // sent: it competes as an outbound candidate exactly like a 'sent' row.
      // Treating it as inbound would make the owner's own newer filed message
      // "answer" the customer and silently drop them from the ledger.
      if (f === 'sent' || (isOutlookFiled(f) && isOutboundMail(e, f))) {
        if (ts > cur.lastOutboundTs) { cur.lastOutbound = e; cur.lastOutboundTs = ts; }
      } else if (f === 'inbox' || isOutlookFiled(f)) {
        // A reply the owner has since filed into an Outlook folder is still a
        // reply: counting only the inbox would keep flagging that customer as
        // "waiting" the moment their answer was tidied away.
        if (ts > cur.lastInbound) cur.lastInbound = ts;
      }
      agg.set(cid, cur);
    }
    const ids = new Set<string>();
    agg.forEach((v) => {
      if (!v.lastOutbound) return;
      if (v.lastOutboundTs <= v.lastInbound) return;          // they've since replied
      if (now - v.lastOutboundTs < WAITING_GRACE_MS) return;  // too recent to nag
      if ((v.lastOutbound as any).waitingDismissed) return;   // owner said stop (replied out-of-band)
      ids.add(v.lastOutbound.id);
    });
    return ids;
  }, [enriched, liveInboxIds, nowTick, getTrueCustomerId]);

  // Folder filter — custom folder ids are prefixed `f_`.
  const visible = useMemo(() => {
    const now = nowTick;
    return searched.filter((e) => {
      // When searching, show all matches regardless of folder
      if (searchTerm.trim()) return true;

      // Snooze: the dedicated view shows only currently-snoozed mail. This
      // branch is evaluated BEFORE the live-window gate below so an email
      // snoozed out of search results (not in liveInboxIds) is still
      // reachable + unsnoozable here — otherwise the badge counts it but the
      // list can never show it.
      const snoozedUntil = (e as any).snoozedUntil as number | null | undefined;
      const isSnoozed = !!snoozedUntil && snoozedUntil > now;
      // (Deleted mail is never listed, snoozed or not - snoozedUntil survives a delete.)
      if (activeFolder === 'snoozed') return isSnoozed && normalizeEmailFolder(e) !== 'deleted';

      // Waiting-on-them ledger: the precomputed set of unanswered outbound mail.
      if (activeFolder === 'waiting') return waitingEmailIds.has(e.id);

      // NOT searching: restrict to the live canonical window. A search merges
      // matching OLD emails into preLoadedEmails and they're deliberately
      // preserved across snapshots — without this gate they keep flooding the
      // inbox/folders after the search is cleared. liveInboxIds is exactly the
      // set the live 30-day subscription covers (the badge + dashboard already
      // gate on it). The size>0 guard avoids blanking before the first snapshot.
      if (liveInboxIds.size > 0 && !liveInboxIds.has(e.id)) return false;

      // Every non-snoozed view hides currently-snoozed mail until it wakes.
      if (isSnoozed) return false;

      const f = normalizeEmailFolder(e);

      if (activeFolder.startsWith('f_')) {
        // Deleted mail never belongs in a custom folder, whatever sender rule
        // or folder name it would otherwise match.
        return f !== 'deleted' && e.virtualFolderId === activeFolder.slice(2);
      }

      if (activeFolder === 'drafts') return false; // local drafts are in userDrafts — handled separately

      // Archive: EVERYTHING the owner filed out of the inbox in Outlook (the
      // Archive folder and any other folder), including mail a custom folder
      // also claims - that is why the custom-folder exclusion below is NOT
      // applied here. The unread chip stays de-duplicated in `counts`.
      if (activeFolder === 'archive') return isOutlookFiled(f);

      // Inbox/Junk: exclude emails that have been routed to a custom folder
      if ((activeFolder === 'inbox' || activeFolder === 'junk') && e.virtualFolderId) {
        return false;
      }

      return f === activeFolder;
    });
  }, [searched, activeFolder, searchTerm, liveInboxIds, nowTick, waitingEmailIds]);

  // Count of currently-snoozed mail — drives the sidebar "snoozed" badge.
  const snoozedCount = useMemo(() => {
    const now = nowTick;
    return enriched.reduce((n, e) => {
      const s = (e as any).snoozedUntil as number | null | undefined;
      // Same rule as the snoozed view, or the badge would count mail the list never shows.
      return n + (s && s > now && normalizeEmailFolder(e) !== 'deleted' ? 1 : 0);
    }, 0);
  }, [enriched, nowTick]);


  // Flagged first, then newest first — `pinned` is the legacy field name.
  const sorted = useMemo(() => {
    return [...visible].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return b.createdAt.getTime() - a.createdAt.getTime();
    });
  }, [visible]);

  // ── Thread grouping (display-only) ────────────────────────────────────
  // Collapse a Graph conversation to ONE row (its newest email) with a count
  // badge; expanding inlines the older messages as indented child rows. Pure
  // presentation over `sorted` — counts, unread badges, mark-all-read and the
  // keyboard cursor all keep operating on the flat data. OFF while searching
  // and in the special views (waiting/snoozed show specific emails on purpose).
  const [groupThreads, setGroupThreads] = useState<boolean>(() => {
    try { return localStorage.getItem('term_mail_threads') !== '0'; } catch { return true; }
  });
  const toggleThreads = useCallback(() => {
    setGroupThreads((v) => {
      try { localStorage.setItem('term_mail_threads', v ? '0' : '1'); } catch { /* ignore */ }
      return !v;
    });
  }, []);
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const toggleThreadExpanded = useCallback((key: string) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  type ThreadedRow = FeedEmailData & {
    __threadKey?: string | null;
    __threadCount?: number;
    __threadHasUnread?: boolean;
    __isThreadChild?: boolean;
  };
  const isSearching = searchTerm.trim().length > 0;
  const threadingActive = groupThreads && !isSearching
    && activeFolder !== 'snoozed' && activeFolder !== 'waiting';

  const threadedData = useMemo<{ rows: ThreadedRow[]; buckets: Map<string, FeedEmailData[]> }>(() => {
    const buckets = new Map<string, FeedEmailData[]>();
    if (!threadingActive) return { rows: sorted as ThreadedRow[], buckets };
    const convKey = (e: FeedEmailData): string | null => (e as any).graph?.conversationId || null;
    // Pass 1: bucket by conversation. PINNED emails stay OUT of buckets — the
    // sort puts them first, so a pinned old email would otherwise become the
    // thread primary and HIDE the conversation's newest message while
    // collapsed. Pinned rows keep their own top slot; the rest of the
    // conversation groups normally (each bucket is newest-first).
    for (const e of sorted) {
      const k = convKey(e);
      if (!k || e.pinned) continue;
      const arr = buckets.get(k);
      if (arr) arr.push(e); else buckets.set(k, [e]);
    }
    // Pass 2: emit — singles/pinned as-is; multi-email conversations once, at
    // the position of their newest email, children inlined when expanded.
    const out: ThreadedRow[] = [];
    const emitted = new Set<string>();
    for (const e of sorted) {
      const k = convKey(e);
      const arr = (k && !e.pinned) ? buckets.get(k) : undefined;
      if (!k || e.pinned || !arr || arr.length < 2) { out.push(e); continue; }
      if (emitted.has(k)) continue;
      emitted.add(k);
      out.push({
        ...e,
        __threadKey: k,
        __threadCount: arr.length,
        __threadHasUnread: arr.some((x) => isUnread(x)),
      });
      if (expandedThreads.has(k)) {
        for (const child of arr.slice(1)) out.push({ ...child, __threadKey: k, __isThreadChild: true });
      }
    }
    return { rows: out, buckets };
  }, [sorted, threadingActive, expandedThreads]);
  const threadedList = threadedData.rows;
  const threadBuckets = threadedData.buckets;
  // Count of top-level rows (children ride along with their primary) — the
  // unit the displayCount window and load-more math operate on.
  const topLevelCount = useMemo(
    () => threadedList.reduce((n, r) => n + ((r as any).__isThreadChild ? 0 : 1), 0),
    [threadedList],
  );

  // The messages a row stands for: a COLLAPSED conversation is every message in it, anything
  // else (a single email, an expanded thread's child row) is just itself. Opening, marking
  // read, archiving, deleting and selecting a thread row all act on this whole set - the
  // unread dot is thread-aggregate, so the action must be too, or "mark read" looks broken
  // (dot stays lit from a hidden child).
  const rowMessages = useCallback((email: FeedEmailData): FeedEmailData[] => {
    const t: any = email;
    const arr = t.__threadKey && t.__threadCount ? threadBuckets.get(t.__threadKey) : null;
    return arr && arr.length ? arr : [email];
  }, [threadBuckets]);
  const rowIdsOf = useCallback(
    (email: FeedEmailData): string[] => rowMessages(email).map(rowKeyOf).filter(Boolean),
    [rowMessages],
  );

  const markThreadRead = useCallback((email: FeedEmailData) => {
    const targets = rowMessages(email);
    for (const e of targets) {
      // auto: opening a row is not a deliberate "mark read" click, so an
      // owner-only refusal (the second user) must not toast on every open.
      if (isUnread(e) && !e.pinned) void markEmailRead(e, { auto: true });
    }
  }, [rowMessages]);

  // Row open = thread-aware mark-read + open the email itself.
  const openRow = useCallback((email: FeedEmailData) => {
    markThreadRead(email);
    openEmailPanel('viewEmail', email as PanelEmailData);
  }, [markThreadRead, openEmailPanel]);

  // When searching, show every match — no pagination. Search is for
  // "find this thing", paging just gets in the way. When browsing
  // (no active search), keep the windowed render so an inbox of
  // thousands doesn't blow up the DOM on first paint.
  const toRender = useMemo(() => {
    if (isSearching) return threadedList;
    // Window by TOP-LEVEL rows only, letting expanded children ride along with
    // their primary — a plain slice could cut a thread's children off right
    // after the user expands it at the window edge.
    const out: ThreadedRow[] = [];
    let topLevel = 0;
    for (const r of threadedList) {
      if (!(r as any).__isThreadChild) {
        if (topLevel >= displayCount) break;
        topLevel++;
      }
      out.push(r);
    }
    return out;
  }, [threadedList, displayCount, isSearching]);

  // Counts per folder — drives the sidebar badges. We count UNREAD only so
  // the numbers match the rest of the app's "you have N new" semantics.
  const counts = useMemo(() => {
    const c: Record<string, number> = { inbox: 0, sent: 0, junk: 0, archive: 0, deleted: 0 };
    enriched.forEach((e) => {
      // Same live-window gate as the list (`visible`) and the launcher badge
      // (computeUnreadInboxCount). Search hits, older pages and date-range
      // loads are merged into preLoadedEmails and kept across snapshots but
      // sit outside liveInboxIds; counting them would leave the chips
      // inflated (by old unread automated mail especially) after a search
      // until a reload, disagreeing with the rows and the badge. The size>0
      // guard avoids blanking the chips before the first snapshot.
      if (liveInboxIds.size > 0 && !liveInboxIds.has(e.id)) return;
      if (!isUnread(e)) return;
      // Snoozed mail is hidden from its folder, so don't badge it there either
      // (it's surfaced by the separate `snoozed` count instead).
      const s = (e as any).snoozedUntil as number | null | undefined;
      if (s && s > nowTick) return;
      const f = normalizeEmailFolder(e);
      // Deleted mail badges ONLY the Deleted view and returns before the custom-folder /
      // inbox bookkeeping below, so it can never inflate another chip (a moved-out doc
      // mislabelled deleted:true used to inflate the inbox count).
      if (f === 'deleted') {
        c.deleted += 1;
        return;
      }
      if (e.virtualFolderId) {
        c[`f_${e.virtualFolderId}`] = (c[`f_${e.virtualFolderId}`] || 0) + 1;
      } else {
        // Filed mail badges ONE place: its custom folder when a rule/name
        // claimed it (above), else the archive view - never both.
        const key = isOutlookFiled(f) ? 'archive' : f;
        c[key] = (c[key] || 0) + 1;
      }
    });
    return c;
  }, [enriched, nowTick, liveInboxIds]);

  const unreadVisibleCount = useMemo(
    () => visible.reduce((sum, e) => sum + (isUnread(e) && !e.pinned ? 1 : 0), 0),
    [visible]
  );

  // (Row opening lives in openRow — defined after the threading memo so it can
  // be thread-aware: opening a collapsed conversation clears its hidden unread
  // children too.)

  // ── Body prefetch ─────────────────────────────────────────────────────────
  // Opening a Graph email cold round-trips to the getGraphEmailBody function +
  // Microsoft Graph — that's the lag the user sees. We warm the body BEFORE the
  // click so the open is instant: proactively for the rows on screen (effect
  // below) and on hover as an accelerator for rows scrolled to. The cache layer
  // queues + dedups, so warming a screenful at once is cheap and safe.
  const warmBody = useCallback((email: FeedEmailData) => {
    if (!canPrefetchBody(email)) return;
    const id = String(email.emailId || email.id || '');
    if (id) warmGraphEmailBodyCache(id, 'html');
  }, []);

  const hoverTimersRef = useRef<Record<string, number>>({});
  const handleRowHover = useCallback((email: FeedEmailData) => {
    if (!canPrefetchBody(email)) return;
    const id = String(email.emailId || email.id || '');
    if (!id || hoverTimersRef.current[id]) return;
    // Small debounce so sweeping the cursor down the list doesn't enqueue every
    // row it passes over — only rows the cursor actually rests on.
    hoverTimersRef.current[id] = window.setTimeout(() => {
      delete hoverTimersRef.current[id];
      warmGraphEmailBodyCache(id, 'html');
    }, 120);
  }, []);
  const handleRowHoverEnd = useCallback((email: FeedEmailData) => {
    const id = String(email.emailId || email.id || '');
    const t = hoverTimersRef.current[id];
    if (t) { window.clearTimeout(t); delete hoverTimersRef.current[id]; }
  }, []);
  useEffect(() => () => {
    Object.values(hoverTimersRef.current).forEach((t) => window.clearTimeout(t));
    hoverTimersRef.current = {};
  }, []);

  // Proactively warm the bodies most likely to be opened next: the rows at the
  // top of the current list (what's above the fold) whenever the list, folder
  // or search changes. The queue drains these at the concurrency cap; rows
  // further down warm on hover. The list isn't virtualized, so slicing the top
  // is a good proxy for "currently visible".
  useEffect(() => {
    const PREWARM_TOP_N = 12;
    for (const email of toRender.slice(0, PREWARM_TOP_N)) warmBody(email);
  }, [toRender, warmBody]);

  const handleMarkRow = useCallback(async (e: React.MouseEvent, email: FeedEmailData) => {
    e.stopPropagation();
    if (!isUnread(email)) return;
    await markEmailRead(email);
  }, []);

  // Revert a bulk mark-as-read — flips the same set back to unread (Graph +
  // Firestore). Powers the "Undo" affordance on the mark-all toast.
  const undoMarkAllRead = useCallback(async (items: Array<{ key: string; source: string }>) => {
    if (items.length === 0) return;
    const failures: string[] = [];
    const BATCH = 8;
    for (let i = 0; i < items.length; i += BATCH) {
      const slice = items.slice(i, i + BATCH);
      await Promise.all(slice.map(async ({ key, source }) => {
        // If Graph refuses the un-read, roll back to READ (the state the undo
        // started from) — never leave Firestore claiming unread while Outlook
        // still shows read.
        const res = await applyEmailReadState(key, source === 'graph', false, { dismissed: true, isRead: true });
        if (!res.ok) failures.push(res.reason);
      }));
    }
    if (failures.length === 0) {
      toast.success('restored to unread');
    } else if (failures.length === items.length) {
      toast.error(`couldn't restore to unread: ${failures[0]}`);
    } else {
      toast.warning(`restored ${items.length - failures.length} of ${items.length} to unread — ${failures.length} failed: ${failures[0]}`);
    }
  }, []);

  // Bulk mark-as-read with a 6s Undo toast (parity with the classic feed). One path for
  // "mark all" and for the bulk bar's [mark read]: same Firestore + Graph writes, same batching,
  // same ONE summary toast.
  const markRowsRead = useCallback(async (targets: FeedEmailData[]) => {
    if (targets.length === 0) return;
    setIsMarkingAll(true);
    const marked: Array<{ key: string; source: string }> = [];
    // Failure reasons, one per email that did NOT end up read (Firestore write
    // refused, or Graph refused and the row was rolled back to unread).
    const failures: string[] = [];
    // Rows without an id cannot be written at all, so they are not "attempted".
    const attempted = targets.filter((e) => rowKeyOf(e)).length;
    try {
      const BATCH = 8;
      for (let i = 0; i < targets.length; i += BATCH) {
        const slice = targets.slice(i, i + BATCH);
        await Promise.all(slice.map(async (email) => {
          const key = String(email.emailId || email.id || '');
          if (!key) return;
          const res = await applyEmailReadState(key, email.source === 'graph', true, readFieldsOf(email));
          if (res.ok) marked.push({ key, source: email.source || 'imap' });
          else failures.push(res.reason);
        }));
      }
    } catch (err) {
      console.error('[TerminalEmailFeed] Mark all as read failed:', err);
    } finally {
      setIsMarkingAll(false);
    }
    // ONE summary for the whole batch — per-email toasts would bury the list.
    const failedText = failures.length > 0
      ? `${failures.length} of ${attempted} couldn't be marked read (${failures[0]})`
      : '';
    if (marked.length === 0 && failures.length > 0) {
      toast.error(failedText);
    } else if (marked.length > 0) {
      const notify = failures.length > 0 ? toast.warning : toast.info;
      notify(
        ({ closeToast }: any) => (
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>Marked {marked.length} as read{failedText ? ` — ${failedText}` : ''}</span>
            <button
              type="button"
              onClick={() => { void undoMarkAllRead(marked); closeToast?.(); }}
              style={{ background: 'none', border: '1px solid currentColor', borderRadius: 4, padding: '1px 8px', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
            >
              Undo
            </button>
          </span>
        ),
        // A partial failure has to stay readable longer than a plain success.
        { autoClose: failures.length > 0 ? 12000 : 6000 },
      );
    }
  }, [undoMarkAllRead]);

  // "Mark all" skips flagged mail (a flag means follow-up); a hand-picked selection does not.
  const handleMarkAllAsRead = useCallback(async () => {
    await markRowsRead(visible.filter((e) => isUnread(e) && !e.pinned));
  }, [visible, markRowsRead]);

  const handleCompose = useCallback(() => {
    // Reset any 'edit' draft mode first — otherwise an abandoned AI/reply draft
    // (draftMode stays 'edit' until a send) would silently pre-fill this "new"
    // compose with the wrong recipient/body. Mirrors DraftsPanel.
    setDraftMode('new');
    setEmailPanelView('composeEmail');
  }, [setEmailPanelView, setDraftMode]);

  const handleOpenDrafts = useCallback(() => {
    setEmailPanelView('drafts');
  }, [setEmailPanelView]);

  const handleLoadMore = useCallback(async () => {
    // First try expanding the local window; if we've exhausted what's in
    // memory, ask the context to pull another slice from Firestore.
    // (topLevelCount, not the flat email count — grouping collapses rows, so
    // the flat count would claim more pages than the list can actually show.)
    if (displayCount < topLevelCount) {
      setDisplayCount((c) => c + 50);
      return;
    }
    if (hasMoreOlderEmails && !isLoadingOlderEmails) {
      await loadOlderEmails();
      setDisplayCount((c) => c + 50);
    }
  }, [displayCount, topLevelCount, hasMoreOlderEmails, isLoadingOlderEmails, loadOlderEmails]);

  // Hide the [load more] / older-emails footer entirely while searching —
  // search results are already rendered in full. The "load older" path
  // also wouldn't fetch more matches anyway; the deep search has already
  // pulled them.
  const hasMoreToShow = !isSearching && (displayCount < topLevelCount || hasMoreOlderEmails);

  // ────────────────────────────────────────────────────────────────────
  // Per-email action handlers
  // ────────────────────────────────────────────────────────────────────
  //
  // These mirror the classic feed's handleTogglePinned / handleToggleJunk
  // / handleToggleReadState / handleDeleteConfirm (plus the optimistic
  // Firestore write + Graph propagation pattern). Kept local in the
  // terminal feed rather than hoisted to EmailPanelContext because the
  // semantics are UI-owned — Firestore is the data layer, these are
  // per-list interactions.

  // Simple pending guard — avoids rapid double-toggles shipping multiple
  // Graph writes or reverting optimistic state unpredictably.
  const setPending = (emailId: string, key: string, on: boolean) => {
    setPendingByEmailId((prev) => ({
      ...prev,
      [emailId]: { ...(prev[emailId] || {}), [key]: on },
    }));
  };
  const isPending = (emailId: string, key: string) =>
    !!pendingByEmailId[emailId]?.[key];

  const handleTogglePin = useCallback(async (e: React.MouseEvent, email: FeedEmailData) => {
    e.stopPropagation();
    const key = String(email.emailId || email.id || '');
    if (!key || isPending(key, 'pin')) return;
    const desired = !email.pinned;
    setPending(key, 'pin', true);
    try {
      const optimisticWrite = email.source === 'graph' ? getGraphFlagWrite(desired) : { pinned: desired };
      await updateDoc(doc(db, 'emails', key), optimisticWrite);
      if (email.source === 'graph') {
        try {
          await authPostConfirmed(SET_GRAPH_FLAG_URL, { emailId: key, flagged: desired });
        } catch (err) {
          // Graph refused: put the flag fields back so the star stops claiming
          // a flag Outlook does not have. A failed rollback must not mask the
          // real reason, so it is logged rather than thrown.
          await updateDoc(doc(db, 'emails', key), getGraphFlagWrite(!desired)).catch((rbErr) => {
            console.error('[TerminalEmailFeed] flag rollback failed:', rbErr);
          });
          throw err;
        }
      }
    } catch (err) {
      console.error('[TerminalEmailFeed] flag toggle failed:', err);
      toast.error(`couldn't ${desired ? 'flag' : 'unflag'}: ${errText(err)}`);
    } finally { setPending(key, 'pin', false); }
  }, [pendingByEmailId]);

  const handleToggleJunk = useCallback(async (e: React.MouseEvent, email: FeedEmailData) => {
    e.stopPropagation();
    const key = String(email.emailId || email.id || '');
    if (!key || isPending(key, 'junk')) return;
    const currentFolder = normalizeEmailFolder(email);
    // Graph won't move sent items / drafts into Junk Email folder — the
    // API returns 500. Gate client-side so the action doesn't fail mid-
    // flight (the button is already hidden for these folders, but
    // someone could still click from a stale view).
    if (currentFolder === 'sent' || currentFolder === 'drafts') {
      toast.error(`can't mark ${currentFolder} items as junk`);
      return;
    }
    const isNowJunk = currentFolder !== 'junk';
    const action = isNowJunk ? 'mark as junk' : 'move to inbox';
    setPending(key, 'junk', true);
    // Graph FIRST, Firestore only after Outlook agreed: a move is not
    // instant-feel enough to justify an optimistic row that jumps folders and
    // then has to jump back. Nothing changes locally unless the server said ok.
    let movedInOutlook = false;
    try {
      if (email.source === 'graph') {
        await authPostConfirmed(TOGGLE_GRAPH_JUNK_URL, { emailId: key, moveToJunk: isNowJunk });
        movedInOutlook = true;
      }
      await updateDoc(doc(db, 'emails', key), {
        folder: isNowJunk ? 'junk' : 'inbox',
        quarantined: isNowJunk,
        updatedAt: serverTimestamp(),
      });
      toast.success(isNowJunk ? 'moved to Junk' : 'moved to Inbox');
    } catch (err: any) {
      console.error('[TerminalEmailFeed] junk toggle failed:', err);
      if (movedInOutlook) {
        // Outlook did move it (the server also stamped the folder); only our
        // local flag write failed. Say so instead of claiming a clean failure.
        toast.warning(`moved in Outlook, but the list didn't update: ${errText(err)}`);
      } else {
        // Surface the real error — generic "failed" doesn't let them tell if
        // it's a permission issue, a stale graph id, etc.
        toast.error(`couldn't ${action}: ${errText(err)}`);
      }
    } finally { setPending(key, 'junk', false); }
  }, [pendingByEmailId]);

  const handleToggleRead = useCallback(async (e: React.MouseEvent, email: FeedEmailData) => {
    e.stopPropagation();
    const key = String(email.emailId || email.id || '');
    if (!key || isPending(key, 'read')) return;
    const wasUnread = isUnread(email);
    const desiredIsRead = wasUnread; // if was unread, set to read; otherwise set to unread
    setPending(key, 'read', true);
    try {
      // Optimistic Firestore write, Graph confirmation, rollback + a single
      // toast on failure — all inside setEmailReadState.
      await setEmailReadState(email, desiredIsRead);
    } finally { setPending(key, 'read', false); }
  }, [pendingByEmailId]);

  // -- Archive / delete / restore: real Outlook moves, reversible ---------
  //
  // Every move goes through mailActions.moveEmail (a Graph move + the Firestore update, done
  // by the server, owner-only). The row is hidden optimistically (pendingMoves) and, once the
  // server says ok, an [undo] toast puts each moved message back where it came from. A move the
  // server refuses brings the row back and says why; the second app user (403) gets no
  // optimistic hide at all and sees the owner-only wording.

  // After rows leave the list, keep the keyboard cursor where the eye is: on the next row that
  // stays. Only when the cursor row itself is going - a menu action on some other row must not
  // make the cursor jump.
  const advanceCursor = useCallback((removed: ReadonlySet<string>) => {
    if (!cursorId || !removed.has(cursorId)) return;
    const keys = toRender.map(rowKeyOf);
    setCursorId(nextCursorKey(keys, keys.indexOf(cursorId), removed));
  }, [cursorId, toRender]);

  // An overlay entry remembers where a row REALLY is in the data (not where the overlay shows
  // it): that is how the sweep knows the snapshot has moved on. Rows handed to an action may
  // already be overlaid (a second move on a row whose first move is still settling), so the
  // signature is read from the raw list.
  const rawEmailsRef = useRef(preLoadedEmails);
  rawEmailsRef.current = preLoadedEmails;
  const rawSignatures = useCallback((emails: ReadonlyArray<FeedEmailData>): Map<string, string> => {
    const want = new Set(emails.map((e) => e.id));
    const sigs = new Map<string, string>();
    for (const r of rawEmailsRef.current) if (want.has(r.id)) sigs.set(r.id, locationSignature(r));
    return sigs;
  }, []);

  const performMove = useCallback(async (
    targets: FeedEmailData[],
    destination: MoveDestination,
    opts: { bulk?: boolean } = {},
  ): Promise<void> => {
    // Only a move still IN FLIGHT blocks another on the same email; a settled one is just
    // waiting for its snapshot and the row is already showing its new home.
    const inFlight = new Set<string>();
    for (const id of Object.keys(pendingMovesRef.current)) {
      if (pendingMovesRef.current[id].phase === 'inflight') inFlight.add(id);
    }
    const plan = planMoves(targets, destination, { skipIds: inFlight });
    if (plan.movable.length === 0) {
      const why = plan.skipped[0]?.reason;
      if (why) toast.info(why, { toastId: MOVE_NOTE_TOAST_ID });
      return;
    }
    // Known owner-only (the second user): no request, no optimistic hide.
    if (isGraphWriteForbiddenNow()) {
      toast.error(OWNER_ONLY_REASON, { toastId: MOVE_FAILED_TOAST_ID });
      return;
    }

    const movable = plan.movable;
    const startedAt = Date.now();
    const sigs = rawSignatures(movable);
    setPendingMoves((prev) => {
      const next = { ...prev };
      for (const e of movable) next[e.id] = { destination, phase: 'inflight', at: startedAt, fromSig: sigs.get(e.id) ?? locationSignature(e) };
      return next;
    });
    advanceCursor(new Set(movable.map(rowKeyOf)));
    if (opts.bulk) setBulkBusy(`moving ${movable.length}...`);

    let out: RunMovesResult<FeedEmailData>;
    const movableIds = movable.map((e) => e.id);
    try {
      out = await runMoves(movable, destination, {
        concurrency: MOVE_CONCURRENCY,
        // Each settled move is a sign of life: keeps the rest of a long batch hidden (an in-flight
        // entry that goes quiet for 30s stops hiding its row), and feeds the bulk bar's progress line.
        onProgress: (done, total) => {
          setPendingMoves((prev) => touchInflight(prev, movableIds, Date.now()));
          if (opts.bulk) setBulkBusy(`moving ${done}/${total}...`);
        },
      });
    } catch {
      // runMoves does not throw; if it ever did, the rows must come back, not vanish.
      out = { succeeded: [], failed: movable.map((email) => ({ email, reason: 'unknown error' })) };
    } finally {
      if (opts.bulk) setBulkBusy(null);
    }

    // Failed rows come back at once. Succeeded rows show at the location the server reported
    // until the Firestore snapshot catches up (bulkMoves.applyPendingMoves).
    const settledAt = Date.now();
    setPendingMoves((prev) => {
      const next = { ...prev };
      for (const f of out.failed) delete next[f.email.id];
      for (const s of out.succeeded) {
        const cur = next[s.email.id];
        if (cur) next[s.email.id] = { ...cur, phase: 'applied', at: settledAt, current: s.result.current };
      }
      return next;
    });

    // The moved rows are done with: untick them (one that stays visible after its move, like a
    // search hit now showing its new folder, would otherwise keep its tick). Rows that FAILED stay
    // ticked - the selection survives the batch - so a retry is one click, not a re-select.
    if (opts.bulk && out.succeeded.length > 0) {
      const moved = new Set(out.succeeded.map((s) => rowKeyOf(s.email)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        moved.forEach((id) => next.delete(id));
        return next.size === prev.size ? prev : next;
      });
    }

    // ONE summary for the whole batch, partial failures listed.
    const summary = moveSummary(destination, out.succeeded.length, out.failed);
    if (out.succeeded.length === 0) {
      toast.error(summary.message, { toastId: MOVE_FAILED_TOAST_ID, autoClose: 6000 });
      return;
    }
    // A partial failure has to stay readable longer than a plain success.
    const autoClose = out.failed.length > 0 ? 12000 : undefined;
    const undoable = undoableMoves(out.succeeded);
    if (undoable.length === 0) {
      // Nothing an undo could reverse (e.g. restored out of Deleted Items).
      (summary.tone === 'warning' ? toast.warning : toast.success)(summary.message, autoClose ? { autoClose } : undefined);
      return;
    }
    showMoveUndoToast({
      message: summary.message,
      autoClose,
      onUndo: async () => {
        // Show each message back where it came from at once (the same overlay as the move
        // itself, so it is right even for mail outside the live window, which never gets a
        // snapshot). A restore the server refuses is put back the way the move left it.
        const undoSigs = rawSignatures(undoable.map((s) => s.email));
        const undoneAt = Date.now();
        setPendingMoves((prev) => {
          const next = { ...prev };
          for (const s of undoable) {
            next[s.email.id] = {
              destination,
              phase: 'applied',
              at: undoneAt,
              fromSig: undoSigs.get(s.email.id) ?? locationSignature(s.email),
              current: s.result.previous,
            };
          }
          return next;
        });
        const res = await undoMoves(undoable, { concurrency: MOVE_CONCURRENCY });
        if (res.failed.length > 0) {
          const stuck = new Set(res.failed.map((f) => f.email.id));
          setPendingMoves((prev) => {
            const next = { ...prev };
            for (const s of undoable) {
              const cur = next[s.email.id];
              if (stuck.has(s.email.id) && cur) next[s.email.id] = { ...cur, current: s.result.current, at: Date.now() };
            }
            return next;
          });
          toast.error(undoFailureText(res.failed), { toastId: UNDO_DETAIL_TOAST_ID, autoClose: 8000 });
        }
        return res.failed.length === 0;
      },
    });
  }, [advanceCursor, rawSignatures]);

  const archiveRow = useCallback((email: FeedEmailData) => performMove(rowMessages(email), 'archive'), [performMove, rowMessages]);
  const deleteRow = useCallback((email: FeedEmailData) => performMove(rowMessages(email), 'deleted'), [performMove, rowMessages]);
  // Back to the inbox: out of Archive / another Outlook folder, or restored from Deleted Items.
  const restoreRow = useCallback((email: FeedEmailData) => performMove(rowMessages(email), 'inbox'), [performMove, rowMessages]);

  // Delete forever - only for mail already in Deleted Items (mailActions refuses anything else).
  // Confirmed first, like the feed's other destructive actions: this one has no undo.
  const purgeRow = useCallback(async (email: FeedEmailData) => {
    const seen = new Set<string>();
    let busy = 0;
    const targets = rowMessages(email).filter((e) => {
      if (!e.id || seen.has(e.id)) return false;
      seen.add(e.id);
      // Only a move still IN FLIGHT blocks this (as in performMove). A settled ('applied') one
      // just waits for its snapshot - for a search hit outside the live window that is never - and
      // the row already shows in Deleted, so refusing here left "Delete forever..." dead all session.
      if (pendingMovesRef.current[e.id]?.phase === 'inflight') { busy += 1; return false; }
      return normalizeEmailFolder(e) === 'deleted';
    });
    if (targets.length === 0) {
      if (busy > 0) toast.info('already being moved', { toastId: MOVE_NOTE_TOAST_ID });
      return;
    }
    if (isGraphWriteForbiddenNow()) {
      toast.error(OWNER_ONLY_REASON, { toastId: MOVE_FAILED_TOAST_ID });
      return;
    }
    const n = targets.length;
    // Name the message: the cursor row is not always the one the eye is on, and this has no undo.
    const subject = String(email.title || (email as any).subject || '').replace(/\s+/g, ' ').trim();
    const named = subject ? `"${subject.length > 50 ? `${subject.slice(0, 49)}...` : subject}"` : '';
    const question = n === 1
      ? `delete ${named || 'this message'} forever? this cannot be undone.`
      : `delete these ${n} messages${named ? ` (${named})` : ''} forever? this cannot be undone.`;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(question)) return;

    const startedAt = Date.now();
    const sigs = rawSignatures(targets);
    // A row that was already moved (settled, waiting for its snapshot) carries an overlay entry
    // that keeps it in its new folder. The purge replaces it; if the purge FAILS the row must go
    // back to that, not to the stale location the raw data still says.
    const prior = new Map<string, PendingMove>();
    for (const e of targets) {
      const p = pendingMovesRef.current[e.id];
      if (p) prior.set(e.id, p);
    }
    setPendingMoves((prev) => {
      const next = { ...prev };
      for (const e of targets) next[e.id] = { destination: 'purge', phase: 'inflight', at: startedAt, fromSig: sigs.get(e.id) ?? locationSignature(e) };
      return next;
    });
    advanceCursor(new Set(targets.map(rowKeyOf)));

    const results = await mapLimit(targets, MOVE_CONCURRENCY, async (e) => {
      try {
        return await deleteEmailForever(toMoveSubject(e));
      } catch {
        return { ok: false as const, reason: 'unknown error' };
      }
    });
    const failures: Array<{ id: string; reason: string }> = [];
    results.forEach((r, i) => { if (!r.ok) failures.push({ id: targets[i].id, reason: r.reason }); });
    const failedIds = new Set(failures.map((f) => f.id));
    const settledAt = Date.now();
    setPendingMoves((prev) => {
      const next = { ...prev };
      for (const e of targets) {
        if (failedIds.has(e.id)) {
          const before = prior.get(e.id);
          if (before) next[e.id] = before; else delete next[e.id];
        } else if (next[e.id]) next[e.id] = { ...next[e.id], phase: 'applied', at: settledAt };
      }
      return next;
    });

    const done = n - failures.length;
    if (failures.length === 0) toast.success(n === 1 ? 'deleted forever' : `deleted ${n} forever`);
    else if (done === 0) toast.error(`couldn't delete forever: ${joinReasons(failures)}`, { toastId: MOVE_FAILED_TOAST_ID, autoClose: 6000 });
    else toast.warning(`deleted ${done} forever, ${failures.length} failed: ${joinReasons(failures)}`, { autoClose: 12000 });
  }, [rowMessages, advanceCursor, rawSignatures]);

  // -- Bulk selection + actions ------------------------------------------
  // Selection is a set of EMAIL ids: ticking a collapsed thread ticks every message in it, it
  // survives snapshots (ids do not change), and rows that leave the list are pruned below.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set<string>();
      for (const e of sorted) present.add(rowKeyOf(e));
      // A row whose move is IN FLIGHT is hidden on purpose (the overlay), not gone: keep its tick.
      // Otherwise the selection empties the moment a batch starts - the desktop bar (and its
      // "moving 7/12..." line) would vanish at once, and rows that fail would come back unticked.
      const pend = pendingMovesRef.current;
      for (const id of Object.keys(pend)) if (pend[id].phase === 'inflight') present.add(id);
      return pruneSelection(prev, present);
    });
  }, [sorted]);

  // Tick / untick a row; shift extends from the last ticked row to this one.
  const handleCheck = useCallback((email: FeedEmailData, extend: boolean) => {
    const key = rowKeyOf(email);
    if (!key) return;
    if (extend && selectAnchorRef.current) {
      const byKey = new Map<string, FeedEmailData>();
      for (const r of toRender) byKey.set(rowKeyOf(r), r);
      const ids = rangeKeys(toRender.map(rowKeyOf), selectAnchorRef.current, key)
        .flatMap((k) => { const r = byKey.get(k); return r ? rowIdsOf(r) : []; });
      setSelectedIds((prev) => addIds(prev, ids));
    } else {
      setSelectedIds((prev) => toggleIds(prev, rowIdsOf(email)));
    }
    selectAnchorRef.current = key;
  }, [toRender, rowIdsOf]);

  const selectRowFromMenu = useCallback((email: FeedEmailData) => {
    setActionMenuFor(null);
    setSelectMode(true);
    handleCheck(email, false);
  }, [handleCheck]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set<string>());
    setSelectMode(false);
    selectAnchorRef.current = null;
  }, []);

  const toggleSelectMode = useCallback(() => {
    if (selecting) clearSelection(); else setSelectMode(true);
  }, [selecting, clearSelection]);

  // Every row of the view, not just the rendered 50-row window: the bar's [select all] says
  // "every row in this view", and the count it then shows is the truth about what an action touches.
  const selectAllRows = useCallback(() => {
    setSelectMode(true);
    setSelectedIds((prev) => addIds(prev, threadedList.flatMap(rowIdsOf)));
  }, [threadedList, rowIdsOf]);

  // The emails behind the ticked ids (every message, including a collapsed thread's hidden ones).
  const selectedEmails = useCallback(
    () => enriched.filter((e) => selectedIds.has(e.id)) as FeedEmailData[],
    [enriched, selectedIds],
  );
  const bulkMove = useCallback(
    (destination: MoveDestination) => { void performMove(selectedEmails(), destination, { bulk: true }); },
    [performMove, selectedEmails],
  );
  const bulkMarkRead = useCallback(
    () => { void markRowsRead(selectedEmails().filter((e) => isUnread(e))); },
    [markRowsRead, selectedEmails],
  );

  // What the bulk bar shows: rows ticked (a conversation counts once), whether every row of the
  // view is ticked, and whether anything ticked is still unread.
  const selectionInfo = useMemo(() => {
    if (!selecting || selectedIds.size === 0) return { rows: 0, allRows: false, canMarkRead: false };
    let rows = 0;
    let shown = 0;
    let ticked = 0;
    for (const r of threadedList) {
      if ((r as any).__isThreadChild) continue;
      shown += 1;
      const s = selectionState(selectedIds, rowIdsOf(r));
      if (s !== 'none') rows += 1;
      if (s === 'all') ticked += 1;
    }
    let canMarkRead = false;
    for (const e of enriched) {
      if (selectedIds.has(e.id) && isUnread(e)) { canMarkRead = true; break; }
    }
    return { rows, allRows: shown > 0 && shown === ticked, canMarkRead };
  }, [selecting, selectedIds, threadedList, rowIdsOf, enriched]);

  // Snooze / unsnooze — stamp (or clear) snoozedUntil. Pure Firestore write;
  // the visible filter hides it until the wake time. Pass null to unsnooze.
  const setSnooze = useCallback(async (email: FeedEmailData, untilMs: number | null) => {
    const key = String(email.emailId || email.id || '');
    if (!key) return;
    try {
      await updateDoc(doc(db, 'emails', key), {
        snoozedUntil: untilMs ? new Date(untilMs) : null,
      });
      setActionMenuFor(null);
      toast.success(untilMs ? `snoozed until ${formatSnoozeLabel(untilMs)}` : 'unsnoozed');
    } catch (err) {
      console.error('[TerminalEmailFeed] snooze failed:', err);
      toast.error(`snooze failed: ${(err as any)?.message || 'unknown error'}`);
    }
  }, []);

  // Dismiss from the waiting-on-them ledger — for customers who replied
  // out-of-band (phone, in person). The flag lives on the SENT email doc; a
  // genuinely newer outbound send is a different doc, so it self-heals.
  const dismissFromWaiting = useCallback(async (email: FeedEmailData) => {
    const key = String(email.emailId || email.id || '');
    if (!key) return;
    try {
      await updateDoc(doc(db, 'emails', key), { waitingDismissed: true });
      setActionMenuFor(null);
      toast.success('dismissed from waiting');
    } catch (err) {
      console.error('[TerminalEmailFeed] dismiss-from-waiting failed:', err);
      toast.error(`dismiss failed: ${(err as any)?.message || 'unknown error'}`);
    }
  }, []);

  // Sender-to-folder: when the user clicks a folder from the [+folder]
  // popover, add the row's sender to that folder's rule list. The enriched
  // memo above re-computes virtualFolderId live, so the row immediately
  // moves to the selected folder on the next render.
  //
  // EXCEPT mail filed in an Outlook folder that a virtual folder mirrors by
  // NAME: resolveVirtualFolderId routes that by the Outlook folder before it
  // ever looks at sender rules, so the new rule cannot move THIS row (it only
  // changes where future mail from the sender lands). Say so, instead of
  // leaving a menu click that looks like a no-op with a hidden side effect.
  const handleAssignSender = useCallback(async (folderId: string, email: FeedEmailData) => {
    const sender = email.from?.[0]?.address || (email as any).senderEmail;
    if (!sender) { toast.error('no sender address'); return; }
    try {
      await addSenderToFolder(folderId, sender);
      setActionMenuFor(null);
      const nameRoutedId = isOutlookFiled(normalizeEmailFolder(email))
        ? matchVirtualFolderByOutlookName(email, customFolders)
        : undefined;
      if (nameRoutedId && nameRoutedId !== folderId) {
        const target = customFolders.find((f) => f.id === folderId)?.name || 'that folder';
        const where = email.outlookFolderName || email.outlookFolderPath || 'its Outlook folder';
        toast.info(`Future inbox mail from ${sender} will go to ${target}. This message stays in ${where}, where it is filed in Outlook.`);
      }
    } catch (err) {
      console.error('[TerminalEmailFeed] assign sender failed:', err);
      toast.error(`assign failed: ${(err as any)?.message || 'unknown error'}`);
    }
  }, [addSenderToFolder, customFolders]);

  // ── Link-to-Job handlers ──────────────────────────────────────────────
  const handleLinkToJob = useCallback(async (emailId: string, jobId: string, jobLabel: string) => {
    setLinkingEmailId(emailId);
    try {
      const resp = await authFetch(
        'https://us-central1-web-construct-app.cloudfunctions.net/linkEmailToJob',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailId, jobId, jobLabel }),
        },
      );
      if (!resp.ok) {
        const text = await resp.text();
        console.error('[TerminalEmailFeed] linkEmailToJob failed:', text);
        toast.error('link failed — see console');
        return;
      }
      toast.success(`linked to ${jobLabel}`);
      // Close any open modals + the row menu so the row reflects the new state.
      setLinkModalOpen(false);
      setLinkModalEmail(null);
      setLinkModalSelectedCustomerId(null);
      setLinkModalSearch('');
      setActionMenuFor(null);
    } catch (err) {
      console.error('[TerminalEmailFeed] linkEmailToJob error:', err);
      toast.error(`link failed: ${(err as any)?.message || 'unknown error'}`);
    } finally {
      setLinkingEmailId(null);
    }
  }, []);

  const openLinkModal = useCallback((email: FeedEmailData) => {
    setLinkModalEmail(email);
    setLinkModalSelectedCustomerId(null);
    setLinkModalSearch('');
    setLinkModalOpen(true);
    setActionMenuFor(null);
  }, []);

  const openCreateJobModal = useCallback((email: FeedEmailData, preselectedCustomerId?: string | null) => {
    const customerId = preselectedCustomerId ?? getTrueCustomerId(email);
    setNewJobEmailContext({ email, customerId: customerId || null });
    setShowNewJobModal(true);
    setActionMenuFor(null);
    setLinkModalOpen(false);
  }, [getTrueCustomerId]);

  const handleNewJobCreated = useCallback(async (job: any) => {
    if (newJobEmailContext?.email) {
      const ctxEmail = newJobEmailContext.email;
      const emailId = ctxEmail.emailId || ctxEmail.id;
      if (emailId && job?.id && job?.jobLabel) {
        await handleLinkToJob(emailId, job.id, job.jobLabel);
      }
    }
    setNewJobEmailContext(null);
    setShowNewJobModal(false);
  }, [newJobEmailContext, handleLinkToJob]);

  // Search-modal customer list — top 10 by alphabetical when no search,
  // otherwise filtered by name/company/email substring (matches default).
  const linkModalCustomerOptions = useMemo(() => {
    const term = linkModalSearch.trim().toLowerCase();
    if (!term) {
      return [...customers]
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
        .slice(0, 10);
    }
    return customers
      .filter((c) =>
        (c.name || '').toLowerCase().includes(term) ||
        (c.company || '').toLowerCase().includes(term) ||
        (c.email || '').toLowerCase().includes(term),
      )
      .slice(0, 20);
  }, [customers, linkModalSearch]);

  const linkModalJobs = useMemo(() => {
    if (!linkModalSelectedCustomerId) return [];
    return allJobs.filter((j) => {
      const cid = (j as any).customerId;
      return Array.isArray(cid) ? cid.includes(linkModalSelectedCustomerId) : cid === linkModalSelectedCustomerId;
    });
  }, [allJobs, linkModalSelectedCustomerId]);

  const handleRemoveSender = useCallback(async (folderId: string, sender: string) => {
    try {
      await removeSenderFromFolder(folderId, sender);
    } catch (err) {
      console.error('[TerminalEmailFeed] remove sender failed:', err);
      toast.error(`remove failed: ${(err as any)?.message || 'unknown error'}`);
    }
  }, [removeSenderFromFolder]);

  // ────────────────────────────────────────────────────────────────────
  // Folder CRUD handlers
  // ────────────────────────────────────────────────────────────────────

  // Commit-once guard: Escape cancels by clearing the folder-edit state,
  // but the input's `onBlur` fires right after. Without a flag, the blur
  // handler would still see the pre-clear state (stale closure) and
  // commit the rename/create anyway. Setting this ref to true in the
  // Escape path makes the blur no-op.
  const editActionConsumedRef = useRef(false);

  const handleCreateFolder = useCallback(async () => {
    if (editActionConsumedRef.current) { editActionConsumedRef.current = false; return; }
    editActionConsumedRef.current = true;
    const name = newFolderName.trim();
    if (!name) { setIsCreatingFolder(false); setNewFolderName(''); return; }
    try {
      const id = await createCustomFolder(name);
      setNewFolderName('');
      setIsCreatingFolder(false);
      // Auto-switch to the newly-created folder so the user lands in it.
      setActiveFolder(`f_${id}`);
    } catch (err) {
      console.error('[TerminalEmailFeed] create folder failed:', err);
      toast.error(`create folder failed: ${(err as any)?.message || 'unknown error'}`);
    }
  }, [newFolderName, createCustomFolder]);

  const handleRenameFolder = useCallback(async () => {
    if (editActionConsumedRef.current) { editActionConsumedRef.current = false; return; }
    editActionConsumedRef.current = true;
    if (!renamingFolderId) return;
    const name = renamingFolderDraft.trim();
    if (!name) { setRenamingFolderId(null); setRenamingFolderDraft(''); return; }
    try {
      await renameCustomFolder(renamingFolderId, name);
    } catch (err) {
      console.error('[TerminalEmailFeed] rename folder failed:', err);
      toast.error(`rename failed: ${(err as any)?.message || 'unknown error'}`);
    } finally {
      setRenamingFolderId(null);
      setRenamingFolderDraft('');
    }
  }, [renamingFolderId, renamingFolderDraft, renameCustomFolder]);

  const handleDeleteFolder = useCallback(async (folderId: string) => {
    // Minimal confirm via window.confirm — keeps us from building a full
    // modal for this destructive-but-reversible action (deleteCustomFolder
    // does a soft delete; row stops appearing in lists).
    const folder = customFolders.find((f) => f.id === folderId);
    const label = folder?.name || 'this folder';
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`delete folder "${label}"?`)) return;
    try {
      await deleteCustomFolder(folderId);
      if (activeFolder === `f_${folderId}`) setActiveFolder('inbox');
    } catch (err) {
      console.error('[TerminalEmailFeed] delete folder failed:', err);
      toast.error(`delete failed: ${(err as any)?.message || 'unknown error'}`);
    }
  }, [customFolders, deleteCustomFolder, activeFolder]);

  // ────────────────────────────────────────────────────────────────────
  // Manual mailbox sync
  // ────────────────────────────────────────────────────────────────────

  const handleSyncMailbox = useCallback(async () => {
    if (isSyncingMailbox) return;
    setIsSyncingMailbox(true);
    flashStatus('checking for new emails…', 'info', null);
    try {
      // checkNewEmails answers HTTP 200 even when individual mailboxes failed —
      // the per-mailbox truth is in results[], so the summary has to read it
      // (a bare "resp.ok -> up to date" hid a mailbox dead for months).
      const data = await authFetchJson<any>(CHECK_NEW_EMAILS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const summary = summarizeSyncResults(data);
      if (summary.allOk) {
        setLastSyncedAt(new Date());
        // All-clear fades after 5s unless another sync is kicked off.
        flashStatus(summary.text, 'info', 5000);
      } else {
        // Partial / failed: stay until the next sync or the user dismisses it.
        flashStatus(summary.text, summary.tone === 'error' ? 'error' : 'warn', null);
      }
    } catch (err) {
      console.error('[TerminalEmailFeed] sync failed:', err);
      flashStatus(`sync failed: ${errText(err)}`, 'error', null);
    } finally {
      setIsSyncingMailbox(false);
      // The manual sync just changed what the server knows about each mailbox.
      void refreshSyncHealth();
    }
  }, [isSyncingMailbox, flashStatus, refreshSyncHealth]);

  // ── Custom folders — flatten groups + children into a simple nav list ──
  const folderNav = useMemo(() => {
    const groups = customFolders.filter((f) => f.isGroup && !f.deleted);
    const leaves = customFolders.filter((f) => !f.isGroup && !f.deleted);
    const orphans = leaves.filter((l) => !l.parentId || !groups.find((g) => g.id === l.parentId));
    return { groups, leaves, orphans };
  }, [customFolders]);

  // Compact per-folder helpers
  const folderKey = (f: { id: string }) => `f_${f.id}`;

  // Human name of the open view - custom folders are keyed `f_<id>`, which must
  // never leak into the statusbar or the empty-state sentence.
  const activeFolderLabel = activeFolder.startsWith('f_')
    ? (customFolders.find((f) => f.id === activeFolder.slice(2))?.name || activeFolder)
    : activeFolder;

  // Derived cursor index — where the anchored email currently sits. Follows the
  // email across prepends/reorders; −1 when there's no cursor or it left the list.
  const cursorIdx = useMemo(
    () => (cursorId ? toRender.findIndex((e) => rowKeyOf(e) === cursorId) : -1),
    [toRender, cursorId],
  );

  // Remember the last valid slot so we can fall back to "the same position"
  // when the anchored email leaves the list (deleted / snoozed away).
  useEffect(() => { if (cursorIdx >= 0) lastCursorIdxRef.current = cursorIdx; }, [cursorIdx]);

  // Anchored email gone but the list isn't empty → re-anchor to the row now
  // occupying the old slot, so the cursor stays where the user was looking.
  useEffect(() => {
    if (!cursorId || cursorIdx >= 0) return;
    if (toRender.length === 0) { setCursorId(null); return; }
    const slot = Math.min(Math.max(0, lastCursorIdxRef.current), toRender.length - 1);
    setCursorId(rowKeyOf(toRender[slot]));
  }, [cursorId, cursorIdx, toRender]);

  // Scroll the cursor row into view when it moves.
  useEffect(() => {
    if (cursorIdx < 0) return;
    const rows = listRef.current?.querySelectorAll('.term-mail__row');
    const el = rows && (rows[cursorIdx] as HTMLElement | undefined);
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursorIdx]);

  // Keyboard triage. The feed stays mounted while the composer column is open
  // beside it, so we bail when a composer/draft view is active — otherwise a
  // focused composer button would let `#`/`e`/`s` act on a background email.
  // We also bail while focus is in a text field or a modal owns the keyboard.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.altKey || ev.ctrlKey || ev.metaKey) return;
      // Bail while the composer COLUMN is visible (checked via the DOM, not
      // emailPanelView — closing the composer doesn't reset that state, so a
      // state-based guard left the feed keyboard dead after the first compose;
      // and a focused composer BUTTON isn't caught by the typing guard below).
      if (document.querySelector('.composer-panel-side.open')) return;
      // The sync-health popover / sheet is open (its own Esc closes it): a
      // focused button inside it must not let j/k/#/e act on a background row.
      if (document.querySelector('.term-mail__sync-details')) return;

      const t = ev.target as HTMLElement | null;
      const tag = (t?.tagName || '').toUpperCase();
      // A row's selection checkbox is a real <input> and a mouse click leaves focus on it, but it
      // is not a text field: counting it as one would switch the whole keyboard off after the first
      // tick (a / r / # / x on the ticked rows, and Esc to clear them, all dead).
      const onRowCheck = tag === 'INPUT' && !!t?.closest('.term-mail__row__check');
      const typing = (tag === 'INPUT' && !onRowCheck) || tag === 'TEXTAREA' || tag === 'SELECT' || !!t?.isContentEditable;

      // '/' focuses search from anywhere (unless already typing in a field).
      if (ev.key === '/' && !typing) {
        ev.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (typing) {
        // In the search box: Esc clears + blurs so the list regains the keyboard.
        if (ev.key === 'Escape' && t === searchInputRef.current) {
          if (searchTerm) setSearchTerm('');
          setHelpOpen(false);
          (t as HTMLElement).blur();
        }
        return;
      }
      // A modal / inline editor owns the keyboard.
      if (linkModalOpen || showNewJobModal || isCreatingFolder || renamingFolderId) return;

      // Enter on a bulk-bar / help / select button or a row checkbox that was reached with the
      // keyboard activates THAT control. Opening the cursor row as well would cancel the control's
      // own click (preventDefault) and mark a client email read as a side effect.
      if (ev.key === 'Enter' && (tag === 'BUTTON' || tag === 'INPUT') && t?.closest(OWN_ENTER_CONTROLS)) return;

      // A held key auto-repeats. Navigation (j / k / arrows) is meant to; the actions are not:
      // each repeat of a / r / # would act on the NEXT row (the cursor advances after a move), so
      // one press held too long archives a run of messages, and x / ? would flip-flop.
      if (ev.repeat && (ev.key === 'a' || ev.key === 'r' || ev.key === '#' || ev.key === 'x' || ev.key === '?')) {
        ev.preventDefault();
        return;
      }

      const n = toRender.length;
      // Resolve the target from the anchored id FRESH each keypress so an action
      // never lands on a row the list shifted under us.
      const idx = cursorId ? toRender.findIndex((e) => rowKeyOf(e) === cursorId) : -1;
      const at = (i: number) => (i >= 0 && i < n ? toRender[i] : null);
      const moveTo = (i: number) => { if (n > 0) setCursorId(rowKeyOf(toRender[Math.min(n - 1, Math.max(0, i))])); };

      switch (ev.key) {
        case 'j':
        case 'ArrowDown':
          ev.preventDefault();
          moveTo(idx < 0 ? 0 : idx + 1);
          break;
        case 'k':
        case 'ArrowUp':
          ev.preventDefault();
          moveTo(idx < 0 ? 0 : idx - 1);
          break;
        case 'Enter': {
          const em = at(idx);
          if (em) { ev.preventDefault(); openRow(em); }
          break;
        }
        case 'e': { // mark read (triage "done") + advance — whole thread if collapsed
          const em = at(idx);
          if (em) {
            ev.preventDefault();
            markThreadRead(em);
            moveTo(idx + 1);
          }
          break;
        }
        case 'u': { // toggle read/unread
          const em = at(idx);
          if (em) { ev.preventDefault(); void handleToggleRead(noopMouseEvt(), em); }
          break;
        }
        case 'p': { // pin / flag
          const em = at(idx);
          if (em) { ev.preventDefault(); void handleTogglePin(noopMouseEvt(), em); }
          break;
        }
        case 'a': { // archive (Outlook's Archive folder, with undo) - the selection if there is one
          if (selectedIds.size > 0) { ev.preventDefault(); bulkMove('archive'); break; }
          const em = at(idx);
          if (em) { ev.preventDefault(); void archiveRow(em); }
          break;
        }
        case 'r': { // back to the inbox: out of Archive / Deleted (restore)
          if (selectedIds.size > 0) { ev.preventDefault(); bulkMove('inbox'); break; }
          const em = at(idx);
          if (em) { ev.preventDefault(); void restoreRow(em); }
          break;
        }
        case 'x': { // tick / untick the cursor row for a bulk action
          const em = at(idx);
          if (em) { ev.preventDefault(); handleCheck(em, false); }
          break;
        }
        case '#': { // delete -> Deleted Items (undo toast); a row already in Deleted: delete FOREVER (confirms first)
          if (selectedIds.size > 0) {
            ev.preventDefault();
            if (activeFolder === 'deleted' && !isSearching) {
              // Delete-forever has no bulk form (the bar hides Delete here too). Purging the CURSOR
              // row instead would hit a row that is not even ticked, and cannot be undone.
              toast.info('delete forever works on one row at a time - untick the rows first', { toastId: MOVE_NOTE_TOAST_ID });
              break;
            }
            bulkMove('deleted');
            break;
          }
          const em = at(idx);
          if (em) {
            ev.preventDefault();
            // Decided by the ROW, not the open view (like the row menu): a search hit that lives in
            // Deleted Items is purged, an inbox hit found while searching from Deleted is just deleted.
            if (normalizeEmailFolder(em) === 'deleted') void purgeRow(em); else void deleteRow(em);
          }
          break;
        }
        case '?': // operator help
          ev.preventDefault();
          setHelpOpen((v) => !v);
          break;
        case 's': { // snooze until tomorrow morning + advance to the next email
          const em = at(idx);
          if (em) {
            ev.preventDefault();
            void setSnooze(em, tomorrowMorningMs());
            moveTo(idx + 1);
          }
          break;
        }
        case 'ArrowRight':
        case 'ArrowLeft': {
          // Expand/collapse the conversation under the cursor.
          const em: any = at(idx);
          const k = em?.__threadKey;
          if (k && (em.__threadCount || em.__isThreadChild)) {
            ev.preventDefault();
            setExpandedThreads((prev) => {
              const next = new Set(prev);
              if (ev.key === 'ArrowRight') next.add(k); else next.delete(k);
              return next;
            });
          }
          break;
        }
        case 'c': // compose
          ev.preventDefault();
          handleCompose();
          break;
        case 'Escape':
          ev.preventDefault();
          // One layer per press: the help panel, then the selection, then the cursor.
          if (helpOpen) setHelpOpen(false);
          else if (selecting) clearSelection();
          else setCursorId(null);
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [
    toRender, cursorId, searchTerm, setSearchTerm, activeFolder, isSearching, selectedIds, selecting, helpOpen,
    linkModalOpen, showNewJobModal, isCreatingFolder, renamingFolderId,
    openRow, markThreadRead, handleToggleRead, handleTogglePin, handleCompose, setSnooze,
    archiveRow, restoreRow, deleteRow, purgeRow, handleCheck, bulkMove, clearSelection,
  ]);

  return (
    <div className={'term-mail' + (selecting && activeFolder !== 'outbox' ? ' term-mail--selecting' : '')}>
      {/* ── Sidebar: folders ─────────────────────────────────────────── */}
      <aside className="term-mail__sidebar">
        <div className="term-mail__sidebar-heading">mailbox</div>

        <div
          className={`term-mail__folder${activeFolder === 'inbox' ? ' term-mail__folder--active' : ''}`}
          onClick={() => setActiveFolder('inbox')}
        >
          <span className="term-mail__folder__prefix">&gt;</span>
          <span className="term-mail__folder__label">inbox</span>
          {counts.inbox > 0 && <span className="term-mail__folder__count term-mail__folder__count--accent">{counts.inbox}</span>}
        </div>

        {(waitingEmailIds.size > 0 || activeFolder === 'waiting') && (
          <div
            className={`term-mail__folder${activeFolder === 'waiting' ? ' term-mail__folder--active' : ''}`}
            onClick={() => setActiveFolder('waiting')}
            title="Client emails you're awaiting a reply to"
          >
            <span className="term-mail__folder__prefix">&gt;</span>
            <span className="term-mail__folder__label">waiting</span>
            {waitingEmailIds.size > 0 && (
              <span className="term-mail__folder__count term-mail__folder__count--accent">{waitingEmailIds.size}</span>
            )}
          </div>
        )}

        <div
          className={`term-mail__folder${activeFolder === 'sent' ? ' term-mail__folder--active' : ''}`}
          onClick={() => setActiveFolder('sent')}
        >
          <span className="term-mail__folder__prefix">&gt;</span>
          <span className="term-mail__folder__label">sent</span>
        </div>

        <div
          className={`term-mail__folder${activeFolder === 'archive' ? ' term-mail__folder--active' : ''}`}
          onClick={() => setActiveFolder('archive')}
          title="Mail you filed out of the inbox in Outlook (Archive and every other Outlook folder)"
        >
          <span className="term-mail__folder__prefix">&gt;</span>
          <span className="term-mail__folder__label">archive</span>
          {counts.archive > 0 && <span className="term-mail__folder__count">{counts.archive}</span>}
        </div>

        <div
          className={`term-mail__folder${activeFolder === 'deleted' ? ' term-mail__folder--active' : ''}`}
          onClick={() => setActiveFolder('deleted')}
          title="Mail you deleted (Outlook's Deleted Items). Restore it, or delete it forever."
        >
          <span className="term-mail__folder__prefix">&gt;</span>
          <span className="term-mail__folder__label">deleted</span>
          {counts.deleted > 0 && <span className="term-mail__folder__count">{counts.deleted}</span>}
        </div>

        <div
          className="term-mail__folder"
          onClick={handleOpenDrafts}
        >
          <span className="term-mail__folder__prefix">&gt;</span>
          <span className="term-mail__folder__label">drafts</span>
          {userDrafts && userDrafts.length > 0 && (
            <span className="term-mail__folder__count term-mail__folder__count--accent">{userDrafts.length}</span>
          )}
        </div>

        <div
          className={`term-mail__folder${activeFolder === 'outbox' ? ' term-mail__folder--active' : ''}`}
          onClick={() => setActiveFolder('outbox')}
          title="Emails scheduled to send later"
        >
          <span className="term-mail__folder__prefix">&gt;</span>
          <span className="term-mail__folder__label">outbox</span>
          {outboxItems.filter((x) => x.status === 'scheduled').length > 0 && (
            <span className="term-mail__folder__count term-mail__folder__count--accent">
              {outboxItems.filter((x) => x.status === 'scheduled').length}
            </span>
          )}
        </div>

        <div
          className={`term-mail__folder${activeFolder === 'junk' ? ' term-mail__folder--active' : ''}`}
          onClick={() => setActiveFolder('junk')}
        >
          <span className="term-mail__folder__prefix">&gt;</span>
          <span className="term-mail__folder__label">junk</span>
          {counts.junk > 0 && <span className="term-mail__folder__count">{counts.junk}</span>}
        </div>

        {(snoozedCount > 0 || activeFolder === 'snoozed') && (
          <div
            className={`term-mail__folder${activeFolder === 'snoozed' ? ' term-mail__folder--active' : ''}`}
            onClick={() => setActiveFolder('snoozed')}
            title="Snoozed emails resurface at their wake time"
          >
            <span className="term-mail__folder__prefix">&gt;</span>
            <span className="term-mail__folder__label">snoozed</span>
            {snoozedCount > 0 && <span className="term-mail__folder__count">{snoozedCount}</span>}
          </div>
        )}

        {/* ── Custom folders section ───────────────────────────────── */}
        {(folderNav.groups.length > 0 || folderNav.orphans.length > 0 || isCreatingFolder) && (
          <div className="term-mail__sidebar-heading" style={{ marginTop: 12 }}>folders</div>
        )}

        {/* Orphan folders (no parent group) — user-editable */}
        {folderNav.orphans.map((f) => {
          const isRenaming = renamingFolderId === f.id;
          if (isRenaming) {
            return (
              <div key={f.id} className="term-mail__folder term-mail__folder--renaming">
                <span className="term-mail__folder__prefix">·</span>
                <input
                  autoFocus
                  className="term-mail__folder__input"
                  value={renamingFolderDraft}
                  onChange={(e) => setRenamingFolderDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleRenameFolder();
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      // Mark consumed BEFORE clearing state so the blur
                      // that follows doesn't re-commit the stale value.
                      editActionConsumedRef.current = true;
                      setRenamingFolderId(null);
                      setRenamingFolderDraft('');
                    }
                  }}
                  onBlur={handleRenameFolder}
                />
              </div>
            );
          }
          return (
            <div
              key={f.id}
              className={`term-mail__folder term-mail__folder--custom${activeFolder === folderKey(f) ? ' term-mail__folder--active' : ''}`}
              onClick={() => setActiveFolder(folderKey(f))}
              title={f.name}
            >
              <span className="term-mail__folder__prefix">·</span>
              <span className="term-mail__folder__label">{f.name}</span>
              {counts[folderKey(f)] > 0 && (
                <span className="term-mail__folder__count term-mail__folder__count--accent">{counts[folderKey(f)]}</span>
              )}
              {/* Hover-reveal actions */}
              <span className="term-mail__folder__actions">
                <button
                  type="button"
                  className="term-mail__folder__action"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRenamingFolderId(f.id);
                    setRenamingFolderDraft(f.name);
                    setIsCreatingFolder(false);
                  }}
                  title="Rename"
                >{br('edit')}</button>
                <button
                  type="button"
                  className="term-mail__folder__action term-mail__folder__action--danger"
                  onClick={(e) => { e.stopPropagation(); handleDeleteFolder(f.id); }}
                  title="Delete"
                >{br('×')}</button>
              </span>
            </div>
          );
        })}

        {/* Groups with children — left as-is for now; rename/delete of
            grouped folders follows the same pattern as orphans so we keep
            orphan editing here and leave the group/child editing as a
            follow-up. Most users don't use groups yet. */}
        {folderNav.groups.map((g) => {
          const children = folderNav.leaves.filter((l) => l.parentId === g.id);
          if (children.length === 0) return null;
          return (
            <div key={g.id} className="term-mail__folder-group">
              <div className="term-mail__folder" style={{ cursor: 'default', color: 'var(--term-fg-dim, #8b8b8b)' }}>
                <span className="term-mail__folder__prefix">▼</span>
                <span className="term-mail__folder__label">{g.name}</span>
              </div>
              {children.map((f) => (
                <div
                  key={f.id}
                  className={`term-mail__folder${activeFolder === folderKey(f) ? ' term-mail__folder--active' : ''}`}
                  onClick={() => setActiveFolder(folderKey(f))}
                  style={{ paddingLeft: 32 }}
                  title={f.name}
                >
                  <span className="term-mail__folder__prefix">·</span>
                  <span className="term-mail__folder__label">{f.name}</span>
                  {counts[folderKey(f)] > 0 && (
                    <span className="term-mail__folder__count term-mail__folder__count--accent">{counts[folderKey(f)]}</span>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        {/* Inline folder creator — slot below the orphans list */}
        {isCreatingFolder ? (
          <div className="term-mail__folder term-mail__folder--renaming">
            <span className="term-mail__folder__prefix">·</span>
            <input
              autoFocus
              className="term-mail__folder__input"
              placeholder="folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateFolder();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  // Mark consumed BEFORE clearing state so the blur that
                  // follows doesn't re-commit the stale value.
                  editActionConsumedRef.current = true;
                  setIsCreatingFolder(false);
                  setNewFolderName('');
                }
              }}
              onBlur={handleCreateFolder}
            />
          </div>
        ) : (
          <button
            type="button"
            className="term-mail__folder-add"
            onClick={() => { setIsCreatingFolder(true); setRenamingFolderId(null); }}
            title="Create a new folder"
          >
            {br('+ new folder')}
          </button>
        )}

        {/* Sender list for the active custom folder — shows who's routed
            into it so the user can un-assign. Only render when a custom
            folder is selected and it has at least one sender rule. */}
        {activeFolder.startsWith('f_') && (() => {
          const f = customFolders.find((cf) => cf.id === activeFolder.slice(2));
          if (!f || !f.senderEmails || f.senderEmails.length === 0) return null;
          return (
            <div className="term-mail__folder-senders">
              <div className="term-mail__folder-senders__head">── senders ──</div>
              {f.senderEmails.map((s) => (
                <div key={s} className="term-mail__folder-senders__row" title={s}>
                  <span className="term-mail__folder-senders__addr">{s}</span>
                  <button
                    type="button"
                    className="term-mail__folder-senders__rm"
                    onClick={() => handleRemoveSender(f.id, s)}
                    title="Remove sender"
                  >{br('×')}</button>
                </div>
              ))}
            </div>
          );
        })()}
      </aside>

      {/* ── Main column ───────────────────────────────────────────────── */}
      <div className="term-mail__main">

        {/* Search + actions */}
        <div className="term-mail__topbar">
          <span className="term-mail__prompt">~/mail $</span>
          <input
            ref={searchInputRef}
            type="text"
            className="term-mail__search"
            placeholder={(isGraphite || isNarrow) ? 'Search mail…' : 'search... ( / to focus · auto-searches entire mailbox)'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            // Operators are typed text (from:, is:unread): a phone keyboard must not capitalise
            // or "correct" them.
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {/* Operator help. Desktop: right beside the box. Phones have no spare width up
              here (search + four 40px buttons on one line): theirs is on the matches line
              while searching, and in the [...] sheet. */}
          {!isNarrow && (
            <button
              type="button"
              className={`term-mail__action term-mail__help-btn${helpOpen ? ' term-mail__action--accent' : ''}`}
              onClick={() => setHelpOpen((v) => !v)}
              title="Search operators (?)"
              aria-label="Search operators"
              aria-expanded={helpOpen}
            >
              {br('?')}
            </button>
          )}
          {searchTerm && (
            <button
              type="button"
              className="term-mail__action"
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              {br('×')}
            </button>
          )}
          {/* Inline status — every typed search fans out to BOTH the
              live Microsoft 365 mailbox (Graph $search) AND the Algolia
              archive (covers legacy IMAP-era emails Graph never had).
              The total reflects deduped hits across both sources. */}
          {searchTerm.trim().length >= 2 && (
            <span
              className={
                'term-mail__search-status'
                + (isDeepSearching ? ' term-mail__search-status--pulse' : '')
              }
              title={localOnlySearch ? LOCAL_ONLY_SEARCH_HINT : undefined}
            >
              {isDeepSearching ? (
                <>
                  <span className="term-mail__search-status__dot" aria-hidden>●</span>
                  {' searching mailbox…'}
                </>
              ) : deepSearchHits !== null
                ? `· ${deepSearchHits} hit${deepSearchHits === 1 ? '' : 's'} across all folders`
                : localOnlySearch ? '· loaded mail only' : ''}
            </span>
          )}
          {isNarrow && !searchTerm && activeFolder !== 'outbox' && (
            <button
              type="button"
              className={`term-mail__action term-mail__select-btn${selecting ? ' term-mail__action--accent' : ''}`}
              onClick={toggleSelectMode}
              title={selecting ? 'Leave selection mode' : 'Select several messages'}
              aria-pressed={selecting}
            >
              {br(selecting ? 'done' : 'select')}
            </button>
          )}
          <button
            type="button"
            className="term-mail__action term-mail__action--accent"
            onClick={handleCompose}
            title="Compose (c)"
          >
            {br(isNarrow ? '+' : '+ compose')}
          </button>
          {/* Desktop keeps the full inline action set. On phones these live
              in the [⋯] sheet below — a one-line topbar that can NEVER wrap
              (search + three 40px targets). */}
          {!isNarrow && (
            <>
              <button
                type="button"
                className="term-mail__action"
                onClick={handleMarkAllAsRead}
                disabled={unreadVisibleCount === 0 || isMarkingAll}
                title={unreadVisibleCount > 0 ? `Mark all ${unreadVisibleCount} as read` : 'Nothing to mark'}
              >
                {br(`mark-all-read${unreadVisibleCount > 0 ? ` ${unreadVisibleCount}` : ''}`)}
              </button>
              <button
                type="button"
                className={`term-mail__action${groupThreads ? ' term-mail__action--accent' : ''}`}
                onClick={toggleThreads}
                title={groupThreads ? 'Conversations are grouped — click to show every email flat' : 'Group emails into conversations'}
              >
                {br(groupThreads ? '≡ threads: on' : '≡ threads: off')}
              </button>
              <button
                type="button"
                className="term-mail__action"
                onClick={handleSyncMailbox}
                disabled={isSyncingMailbox}
                title={[
                  lastSyncedAt
                    ? `check for new emails · last synced ${formatTimeAgo(lastSyncedAt)}`
                    : 'check for new emails from Microsoft 365',
                  // Only when healthy: the pill next to this button owns the
                  // unhealthy story, so desktop chrome stays unchanged.
                  healthyTitle(syncHealth),
                ].filter(Boolean).join(' · ')}
              >
                {isSyncingMailbox ? br('↻ syncing…') : br('↻ sync')}
              </button>
              <MailSyncHealthPill
                health={syncHealth}
                error={syncHealthError}
                loading={syncHealthLoading}
                onRefresh={() => { void refreshSyncHealth(); }}
                br={br}
              />
            </>
          )}
          {/* Phone overflow: mark-all-read / sync / threads in a bottom sheet
              (same idiom as the row [⋮] menu). */}
          {isNarrow && (
            <span className="term-mail__row__menu-wrap term-mail__topmenu">
              <button
                type="button"
                className="term-mail__action"
                onClick={() => setTopMenuOpen((v) => !v)}
                title="More actions"
                aria-label="More actions"
                aria-expanded={topMenuOpen}
              >
                {br('⋯')}
              </button>
              {topMenuOpen && (
                <div
                  className="term-mail__row__menu-scrim"
                  onClick={(e) => { e.stopPropagation(); setTopMenuOpen(false); }}
                />
              )}
              {topMenuOpen && (
                <div className="term-mail__row__menu" onClick={(e) => e.stopPropagation()}>
                  {/* Real sync trouble (warn / error) goes FIRST: it is the one
                      item that says the list itself may be stale. A parked
                      paused-only condition sits at the bottom instead. Hidden
                      while everything is ok. */}
                  {syncMenuItem && !syncHealthQuiet && (
                    <>
                      {syncMenuItem}
                      <div className="term-mail__row__menu-divider" />
                    </>
                  )}
                  <button
                    type="button"
                    className="term-mail__row__menu-item"
                    disabled={unreadVisibleCount === 0 || isMarkingAll}
                    onClick={() => { setTopMenuOpen(false); void handleMarkAllAsRead(); }}
                  >
                    {isMarkingAll ? 'Marking…' : `Mark all as read${unreadVisibleCount > 0 ? ` (${unreadVisibleCount})` : ''}`}
                  </button>
                  <button
                    type="button"
                    className="term-mail__row__menu-item"
                    disabled={isSyncingMailbox}
                    onClick={() => { setTopMenuOpen(false); void handleSyncMailbox(); }}
                  >
                    {isSyncingMailbox ? 'Syncing…' : 'Check for new mail'}
                  </button>
                  <button
                    type="button"
                    className="term-mail__row__menu-item"
                    onClick={() => { setTopMenuOpen(false); toggleThreads(); }}
                  >
                    {groupThreads ? 'Threads: on — tap to show flat' : 'Threads: off — tap to group'}
                  </button>
                  <button
                    type="button"
                    className="term-mail__row__menu-item"
                    onClick={() => { setTopMenuOpen(false); setHelpOpen(true); }}
                  >
                    Search operators (from:, is:unread, in:...)
                  </button>
                  {syncMenuItem && syncHealthQuiet && (
                    <>
                      <div className="term-mail__row__menu-divider" />
                      {syncMenuItem}
                    </>
                  )}
                </div>
              )}
            </span>
          )}
          {/* Close lives HERE on phones — the outer panel header (whose only
              function on the feed was this ✕) is hidden on mobile to reclaim
              a full row of chrome. */}
          {isNarrow && (
            <button
              type="button"
              className="term-mail__action"
              onClick={closeEmailPanel}
              title="Close mail"
              aria-label="Close mail"
            >
              {br('✕')}
            </button>
          )}
        </div>

        {/* Search operators - tap one to drop it into the search box. Inline (no modal):
            a short list that works with a thumb and closes on Esc / ? / tapping the x. */}
        {helpOpen && (
          <div className="term-mail__help" role="region" aria-label="Search operators">
            <div className="term-mail__help__head">
              <span>search operators — tap to add</span>
              <button
                type="button"
                className="term-mail__help__close"
                onClick={() => setHelpOpen(false)}
                aria-label="Close search help"
              >
                {br('×')}
              </button>
            </div>
            <div className="term-mail__help__list">
              {SEARCH_OPERATOR_HELP.map((h) => (
                <button
                  key={h.example}
                  type="button"
                  className="term-mail__help__item"
                  onClick={() => {
                    setSearchTerm(appendToQuery(searchTerm, h.insert));
                    setHelpOpen(false);
                    searchInputRef.current?.focus();
                  }}
                >
                  <code className="term-mail__help__example">{h.example}</code>
                  <span className="term-mail__help__note">{h.note}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Mobile folder switcher — BELOW the search bar (the mobile-mail
            idiom: search on top, filter chips under it). Shown only when the
            sidebar is hidden. Real pills with count badges, ordered by how
            the owner triages: inbox → waiting → snoozed → drafts → sent →
            archive → deleted → outbox → junk, then a divider and the custom folders (without which
            rule-routed mail is invisible on a phone). */}
        <div className="term-mail__mobile-folder">
          {(() => {
            const chip = (
              key: string,
              label: string,
              count: number,
              active: boolean,
              onClick: () => void,
            ) => (
              <button
                key={key}
                type="button"
                className={`term-mail__mobile-folder__chip${active ? ' term-mail__mobile-folder__chip--active' : ''}`}
                onClick={onClick}
              >
                {label}
                {count > 0 && <span className="term-mail__mobile-folder__count">{count}</span>}
              </button>
            );
            const core = [
              chip('inbox', 'inbox', counts.inbox || 0, activeFolder === 'inbox', () => setActiveFolder('inbox')),
              ...((waitingEmailIds.size > 0 || activeFolder === 'waiting')
                ? [chip('waiting', 'waiting', waitingEmailIds.size, activeFolder === 'waiting', () => setActiveFolder('waiting'))]
                : []),
              ...((snoozedCount > 0 || activeFolder === 'snoozed')
                ? [chip('snoozed', 'snoozed', snoozedCount, activeFolder === 'snoozed', () => setActiveFolder('snoozed'))]
                : []),
              chip('drafts', 'drafts', userDrafts?.length || 0, false, handleOpenDrafts),
              chip('sent', 'sent', 0, activeFolder === 'sent', () => setActiveFolder('sent')),
              chip('archive', 'archive', counts.archive || 0, activeFolder === 'archive', () => setActiveFolder('archive')),
              chip('deleted', 'deleted', counts.deleted || 0, activeFolder === 'deleted', () => setActiveFolder('deleted')),
              chip('outbox', 'outbox', 0, activeFolder === 'outbox', () => setActiveFolder('outbox')),
              chip('junk', 'junk', counts.junk || 0, activeFolder === 'junk', () => setActiveFolder('junk')),
            ];
            const custom = [
              ...folderNav.orphans.map((f) =>
                chip(f.id, f.name, counts[folderKey(f)] || 0, activeFolder === folderKey(f), () => setActiveFolder(folderKey(f)))),
              ...folderNav.groups.flatMap((g) =>
                folderNav.leaves.filter((l) => l.parentId === g.id).map((f) =>
                  chip(f.id, f.name, counts[folderKey(f)] || 0, activeFolder === folderKey(f), () => setActiveFolder(folderKey(f))))),
            ];
            return (
              <>
                {core}
                {custom.length > 0 && (
                  <span className="term-mail__mobile-folder__sep" aria-hidden>│</span>
                )}
                {custom}
              </>
            );
          })()}
        </div>

        {/* Sync status line — visible while syncing, briefly after when all is
            well; a partial/failed sync stays (amber/red) until the next sync
            or the user dismisses it. */}
        {(isSyncingMailbox || syncStatus) && (
          <div
            className={
              'term-mail__sync-strip'
              + (!isSyncingMailbox && syncTone !== 'info' ? ` term-mail__sync-strip--${syncTone}` : '')
            }
            role={!isSyncingMailbox && syncTone !== 'info' ? 'alert' : undefined}
          >
            <span className="term-mail__sync-strip__dot" />
            <span className="term-mail__sync-strip__text">{syncStatus || 'working…'}</span>
            {!isSyncingMailbox && lastSyncedAt && (
              <span className="term-mail__sync-strip__meta">
                · {syncTone === 'info' ? 'last synced' : 'last full sync'} {formatTimeAgo(lastSyncedAt)}
              </span>
            )}
            {!isSyncingMailbox && syncTone !== 'info' && (
              <button
                type="button"
                className="term-mail__sync-strip__close"
                onClick={() => flashStatus(null)}
                title="Dismiss"
                aria-label="Dismiss sync status"
              >
                {br('×')}
              </button>
            )}
          </div>
        )}

        {/* Phones: persistent sync-health strip (only when a mailbox is not
            syncing) + its details sheet. Desktop uses the topbar pill instead. */}
        {isNarrow && (
          <>
            <MailSyncHealthStrip health={syncHealth} onOpen={() => setSyncSheetOpen(true)} />
            <MailSyncHealthSheet
              health={syncHealth}
              error={syncHealthError}
              loading={syncHealthLoading}
              open={syncSheetOpen}
              onClose={closeSyncSheet}
              onRefresh={() => { void refreshSyncHealth(); }}
            />
          </>
        )}

        {/* Status row — current folder + count. On phones it's pure
            redundancy (the highlighted chip above already names the folder
            and carries its count), so the whole row is skipped there —
            EXCEPT while searching, when the matches count earns its keep. */}
        {(!isNarrow || !!searchTerm) && (
          <div className="term-mail__statusbar">
            {!isNarrow && (
              <>
                <span className="term-mail__statusbar__label">folder:</span>
                <span className="term-mail__statusbar__value">{activeFolderLabel}</span>
                <span className="term-mail__statusbar__sep">│</span>
                <span className="term-mail__statusbar__label">messages:</span>
                <span className="term-mail__statusbar__value">
                  {activeFolder === 'outbox' ? outboxItems.length : sorted.length}
                </span>
                {unreadVisibleCount > 0 && (
                  <>
                    <span className="term-mail__statusbar__sep">│</span>
                    <span className="term-mail__statusbar__label">unread:</span>
                    <span className="term-mail__statusbar__value">{unreadVisibleCount}</span>
                  </>
                )}
              </>
            )}
            <span className="term-mail__statusbar__spacer" />
            {searchTerm ? (
              <>
                <span>
                  matches <strong style={{ color: 'var(--term-fg, #d4d4d4)' }}>{sorted.length}</strong>
                  {localOnlySearch && <span title={LOCAL_ONLY_SEARCH_HINT}> · loaded mail only</span>}
                </span>
                {/* Phones: the two search-time tools that do not fit the one-line topbar. */}
                {isNarrow && (
                  <>
                    <button
                      type="button"
                      className="term-mail__action term-mail__statusbar__btn"
                      onClick={() => setHelpOpen((v) => !v)}
                      aria-expanded={helpOpen}
                    >
                      {br('? operators')}
                    </button>
                    {activeFolder !== 'outbox' && (
                      <button
                        type="button"
                        className={`term-mail__action term-mail__statusbar__btn${selecting ? ' term-mail__action--accent' : ''}`}
                        onClick={toggleSelectMode}
                        aria-pressed={selecting}
                      >
                        {br(selecting ? 'done' : 'select')}
                      </button>
                    )}
                  </>
                )}
              </>
            ) : (
              /* Keyboard-triage hint — shares the statusbar row (no dedicated
                 strip = one less line of chrome); desktop only. `e` is mark-read
                 (the feed's triage key); archive is `a`. In Archive / Deleted the
                 key that matters is the one that brings mail back. */
              <span className="term-mail__kbhint" aria-hidden>
                <kbd>j</kbd>/<kbd>k</kbd> move · <kbd>↵</kbd> open · <kbd>e</kbd> read ·{' '}
                {activeFolder === 'archive'
                  ? <><kbd>r</kbd> to inbox</>
                  : activeFolder === 'deleted'
                    ? <><kbd>r</kbd> restore</>
                    : <><kbd>a</kbd> archive</>}
                {' '}· <kbd>#</kbd> {activeFolder === 'deleted' ? 'delete forever' : 'delete'} · <kbd>x</kbd> select · <kbd>u</kbd> unread ·{' '}
                <kbd>p</kbd> pin · <kbd>s</kbd> snooze · <kbd>/</kbd> search · <kbd>?</kbd> operators · <kbd>c</kbd> compose
                {threadingActive && <> · <kbd>→</kbd>/<kbd>←</kbd> thread</>}
              </span>
            )}
          </div>
        )}

        {/* List */}
        <div className="term-mail__list" ref={listRef}>
          {activeFolder === 'outbox' ? (
            // ── Outbox (scheduled sends) — its own render path; these rows
            //    live in email_outbox, not the emails collection. ──
            outboxLoading ? (
              <div className="term-mail__loading">loading outbox...</div>
            ) : outboxItems.length === 0 ? (
              <div className="term-mail__empty">
                no scheduled emails — use <strong>[⏰ later]</strong> in the composer
              </div>
            ) : (
              outboxItems.map((item) => {
                const when = item.sendAt ? new Date(item.sendAt) : null;
                const toDisplay = Array.isArray(item.to) ? item.to.join(', ') : String(item.to || '');
                return (
                  <div key={item.id} className="term-mail__row term-mail__row--outbound" style={{ cursor: 'default' }}>
                    <span className="term-mail__row__dot">{item.status === 'error' ? '✗' : '⏰'}</span>
                    <span className="term-mail__row__date" style={{ width: 'auto', minWidth: 56 }}>
                      {when ? `${formatShortDate(when)} ${when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : '—'}
                    </span>
                    <span className="term-mail__row__sender" title={toDisplay}>
                      <span className="term-mail__row__sender-prefix">to: </span>{toDisplay || '—'}
                    </span>
                    <span className="term-mail__row__subject">
                      <span className="term-mail__row__subject-text">{item.subject || '(no subject)'}</span>
                      {item.attachmentCount > 0 && (
                        <span className="term-mail__row__snippet"> — {item.attachmentCount} attachment{item.attachmentCount === 1 ? '' : 's'}</span>
                      )}
                      {item.status === 'error' && (
                        <span className="term-mail__row__snippet" style={{ color: 'var(--bs-danger)' }}> — failed: {item.lastError || 'unknown'}</span>
                      )}
                    </span>
                    {(item.status === 'scheduled' || item.status === 'error') && (
                      <button
                        type="button"
                        className="term-mail__action"
                        onClick={() => void cancelOutboxItem(item.id)}
                        disabled={outboxCancelling !== null}
                        title="Cancel this scheduled send"
                      >
                        {outboxCancelling === item.id ? br('…') : br('cancel')}
                      </button>
                    )}
                  </div>
                );
              })
            )
          ) : isLoadingInitialEmails ? (
            <div className="term-mail__loading">loading mailbox...</div>
          ) : toRender.length === 0 ? (
            <div className="term-mail__empty">
              {searchTerm
                ? <>no matches for <strong>"{searchTerm}"</strong></>
                : <>no messages in <strong>{activeFolderLabel}</strong></>}
            </div>
          ) : (
            toRender.map((email, rowIdx) => {
              const unread = isUnread(email);
              const f = normalizeEmailFolder(email);
              const fClass =
                f === 'sent'   ? ' term-mail__row--sent'   :
                f === 'drafts' ? ' term-mail__row--draft'  :
                f === 'deleted' ? ' term-mail__row--deleted' :
                f === 'junk'   ? ' term-mail__row--junk'   : '';
              const rowKey = email.emailId || email.id;
              const popoverOpen = actionMenuFor === rowKey;
              // Bulk selection: a collapsed thread row is ticked when ALL its messages are.
              const selState = selecting ? selectionState(selectedIds, rowIdsOf(email)) : 'none';
              const rowClass =
                'term-mail__row' +
                (unread ? ' term-mail__row--unread' : '') +
                (email.pinned ? ' term-mail__row--pinned' : '') +
                (popoverOpen ? ' term-mail__row--popover' : '') +
                (rowIdx === cursorIdx ? ' term-mail__row--cursor' : '') +
                (selState !== 'none' ? ' term-mail__row--selected' : '') +
                fClass;

              // Folder tag is redundant when the row's folder matches the
              // active sidebar folder — drop it. We still show it for
              // search results across folders + custom-folder views.
              // Filed mail carries its real Outlook folder name ("trinity"),
              // not the generic "filed"/"archive".
              const isCustomFolder    = activeFolder.startsWith('f_');
              const isFiledRow        = isOutlookFiled(f);
              const folderTag         = emailFolderTag(email);
              const showFolderTag     = isCustomFolder
                // A custom folder named after the Outlook folder would just
                // repeat its own title on every row.
                ? (f !== 'inbox' && folderTag.toLowerCase() !== activeFolderLabel.toLowerCase())
                // The archive view mixes the Archive folder with every other
                // Outlook folder, so every row says where it lives.
                : (activeFolder === 'archive' || f !== activeFolder /* hide when matching */ );
              // Phones hide the tag column; the same fact becomes a chip there.
              const showFolderWhere   = showFolderTag && isFiledRow;
              const folderTitle       = email.outlookFolderPath || folderTag;
              const isOutbound        = isOutboundMail(email, f);
              const contact           = contactDisplay(email, f);
              const contactPrefix     = isOutbound ? 'to: ' : '';
              const snippet           = snippetText(email);
              const rawSubject        = (email.title || '').trim();
              // When there's no subject, promote the snippet to primary
              // (italic + slightly dimmer) so the row has SOMETHING readable.
              // Only fall back to "(no subject)" when there's nothing else.
              const subjectIsEmpty    = !rawSubject;
              const subject           = subjectIsEmpty
                ? (snippet || '(no subject)')
                : rawSubject;
              const showInlineSnippet = !subjectIsEmpty && !!snippet;

              // Business-context chips: overdue money owed (red), linked job,
              // or — when neither — the matched client name. One or two chips
              // max so the dense row stays scannable.
              const chipCid      = getTrueCustomerId(email);
              const chipOverdue  = chipCid ? overdueByCustomer.get(chipCid) : undefined;
              const chipJobLabel = (email as any).linkedJobLabel || null;
              const chipClient   = chipCid ? (customerNameById.get(chipCid) || null) : null;
              const hasChips     = !!(showFolderWhere || chipOverdue || chipJobLabel || (!chipOverdue && !chipJobLabel && chipClient));
              const isWaitingView = activeFolder === 'waiting';
              const tRow          = email as any;
              const threadKey     = tRow.__threadKey as string | undefined;
              const threadCount   = tRow.__threadCount as number | undefined;
              const isThreadChild = tRow.__isThreadChild === true;
              const threadOpen    = !!threadKey && expandedThreads.has(threadKey);

              return (
                <div
                  key={(email.emailId || email.id) + (isThreadChild ? ':child' : '')}
                  className={
                    rowClass
                    + (isOutbound ? ' term-mail__row--outbound' : '')
                    + (isThreadChild ? ' term-mail__row--thread-child' : '')
                  }
                  onClick={(ev) => {
                    // Phone select mode: a tap ticks the row instead of opening it. Desktop:
                    // shift-click extends the selection from the last ticked row.
                    if ((isNarrow && selecting) || (!isNarrow && ev.shiftKey)) {
                      handleCheck(email, !isNarrow && ev.shiftKey);
                      return;
                    }
                    openRow(email);
                  }}
                  onMouseEnter={() => handleRowHover(email)}
                  onMouseLeave={() => handleRowHoverEnd(email)}
                  // Touch has no hover: start the body prefetch at finger-down
                  // so the open that follows ~100-300ms later hits a warm cache.
                  // A move (scroll flick) cancels it — same debounce semantics
                  // as the pointer sweeping past rows.
                  onTouchStart={() => handleRowHover(email)}
                  onTouchMove={() => handleRowHoverEnd(email)}
                  title={email.title}
                >
                  {/* Selection checkbox. Desktop: appears over the unread dot on hover / keyboard
                      focus, and as its own column while anything is selected. Phones: a column
                      in select mode (the whole row is the tap target there). */}
                  <label
                    className="term-mail__row__check"
                    onClick={(e) => e.stopPropagation()}
                    title={selState === 'all' ? 'Deselect' : 'Select (shift-click for a range)'}
                  >
                    <input
                      type="checkbox"
                      readOnly
                      checked={selState === 'all'}
                      ref={(el) => { if (el) el.indeterminate = selState === 'some'; }}
                      aria-label={selState === 'all' ? 'Deselect message' : 'Select message'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCheck(email, e.shiftKey);
                        // A pointer click (detail > 0) must not leave focus parked on the checkbox: it
                        // would keep the row's checkbox lit and the keyboard pointed at a form control.
                        if (e.detail > 0) e.currentTarget.blur();
                      }}
                    />
                  </label>
                  {isThreadChild && <span className="term-mail__row__thread-indent" aria-hidden>└</span>}
                  <span className="term-mail__row__dot">{unread || (!isThreadChild && tRow.__threadHasUnread) ? '●' : ' '}</span>
                  <span className="term-mail__row__date">{formatShortDate(email.createdAt)}</span>
                  {!isThreadChild && !!threadCount && (
                    <button
                      type="button"
                      className="term-mail__row__thread-badge"
                      onClick={(e) => { e.stopPropagation(); toggleThreadExpanded(threadKey!); }}
                      title={threadOpen ? 'Collapse conversation' : `${threadCount} messages in this conversation — expand`}
                      aria-expanded={threadOpen}
                    >
                      {threadOpen ? '▾' : '▸'} {threadCount}
                    </button>
                  )}
                  {showFolderTag && (
                    <span
                      className={'term-mail__row__folder' + (isFiledRow ? ' term-mail__row__folder--outlook' : '')}
                      title={isFiledRow ? `Outlook folder: ${folderTitle}` : undefined}
                    >
                      {br(folderTag)}
                    </span>
                  )}
                  {email.pinned && <span className="term-mail__row__pin" title="Flagged">★</span>}
                  {email.attachments && email.attachments.length > 0 && (
                    <span className="term-mail__row__attach" title="Has attachments">@</span>
                  )}
                  <span className="term-mail__row__sender">
                    {contactPrefix && <span className="term-mail__row__sender-prefix">{contactPrefix}</span>}
                    {searchTerms.length > 0 ? highlight(contact, searchTerms) : contact}
                  </span>
                  <span className="term-mail__row__subject">
                    <span
                      className={
                        'term-mail__row__subject-text'
                        + (subjectIsEmpty ? ' term-mail__row__subject-text--fallback' : '')
                      }
                    >
                      {searchTerms.length > 0 ? highlight(subject, searchTerms) : subject}
                    </span>
                    {showInlineSnippet && (
                      <span className="term-mail__row__snippet">
                        {' — '}
                        {searchTerms.length > 0 ? highlight(snippet, searchTerms) : snippet}
                      </span>
                    )}
                  </span>

                  {/* Business-context chips */}
                  {hasChips && (
                    <span className="term-mail__row__chips">
                      {/* Phones only (CSS): the tag column above is hidden on the
                          stacked row, so filed mail names its Outlook folder here. */}
                      {showFolderWhere && (
                        <span className="term-mail__chip term-mail__chip--folder" title={`Outlook folder: ${folderTitle}`}>
                          in {folderTag}
                        </span>
                      )}
                      {chipOverdue && (
                        <span
                          className="term-mail__chip term-mail__chip--overdue"
                          title={`${chipOverdue.count} overdue invoice${chipOverdue.count === 1 ? '' : 's'} · ${shortMoney(chipOverdue.amount)} owed`}
                        >
                          {shortMoney(chipOverdue.amount)} overdue
                        </span>
                      )}
                      {chipJobLabel && (
                        <span className="term-mail__chip term-mail__chip--job" title={chipJobLabel}>
                          ⚙ {chipJobLabel}
                        </span>
                      )}
                      {!chipOverdue && !chipJobLabel && chipClient && (
                        <span className="term-mail__chip term-mail__chip--client" title={chipClient}>
                          {chipClient}
                        </span>
                      )}
                    </span>
                  )}

                  {/* Single-trigger action menu. Mirrors the default
                      theme dropdown — pin, mark read/unread, junk,
                      move-to-folder, delete. Click-outside dismisses
                      via the document mousedown listener above. */}
                  <span className="term-mail__row__menu-wrap">
                    <button
                      type="button"
                      className="term-mail__row__menu-trigger"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActionMenuFor(popoverOpen ? null : rowKey);
                      }}
                      title="Actions"
                      aria-label="Actions"
                      aria-expanded={popoverOpen}
                    >{br('⋮')}</button>
                    {/* Scrim (phones only via CSS): the menu is a fixed bottom
                        sheet there, and a dismissing tap on the list would
                        otherwise fall through and OPEN whatever row was under
                        the finger. The scrim swallows it. */}
                    {popoverOpen && (
                      <div
                        className="term-mail__row__menu-scrim"
                        onClick={(e) => { e.stopPropagation(); setActionMenuFor(null); }}
                      />
                    )}
                    {popoverOpen && (
                      <div
                        className="term-mail__row__menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Triage moves first: they are what the sheet is opened for on a phone.
                            A collapsed conversation moves as a whole. */}
                        {isMovableEmail(email) && !alreadyAt(email, 'archive') && (
                          <button
                            type="button"
                            className="term-mail__row__menu-item"
                            onClick={(e) => { e.stopPropagation(); setActionMenuFor(null); void archiveRow(email); }}
                            title="Move to Outlook's Archive folder (you can undo)"
                          >
                            Archive{threadCount ? ` conversation (${threadCount})` : ''}
                          </button>
                        )}
                        {isMovableEmail(email) && f !== 'junk' && !alreadyAt(email, 'inbox') && (
                          <button
                            type="button"
                            className="term-mail__row__menu-item"
                            onClick={(e) => { e.stopPropagation(); setActionMenuFor(null); void restoreRow(email); }}
                          >
                            {f === 'deleted' ? 'Restore to Inbox' : 'Move to Inbox'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="term-mail__row__menu-item"
                          onClick={(e) => { e.stopPropagation(); selectRowFromMenu(email); }}
                        >
                          {selState === 'all' ? 'Deselect' : 'Select'}
                        </button>
                        <div className="term-mail__row__menu-divider" />
                        <button
                          type="button"
                          className="term-mail__row__menu-item"
                          onClick={(e) => { handleTogglePin(e, email); setActionMenuFor(null); }}
                        >
                          {email.pinned ? 'Remove flag' : 'Flag'}
                        </button>
                        <button
                          type="button"
                          className="term-mail__row__menu-item"
                          onClick={(e) => { handleToggleRead(e, email); setActionMenuFor(null); }}
                        >
                          {unread ? 'Mark as Read' : 'Mark as Unread'}
                        </button>
                        {isWaitingView && (
                          <button
                            type="button"
                            className="term-mail__row__menu-item"
                            onClick={(e) => { e.stopPropagation(); void dismissFromWaiting(email); }}
                            title="They replied by phone / in person — stop tracking this one"
                          >
                            Dismiss from Waiting
                          </button>
                        )}
                        {f !== 'sent' && f !== 'drafts' && (
                          <button
                            type="button"
                            className="term-mail__row__menu-item"
                            onClick={(e) => { handleToggleJunk(e, email); setActionMenuFor(null); }}
                          >
                            {f === 'junk' ? 'Not Junk (Move to Inbox)' : 'Mark as Junk'}
                          </button>
                        )}

                        {/* Snooze — hide until a wake time, then it resurfaces. */}
                        {(() => {
                          const s = (email as any).snoozedUntil as number | null | undefined;
                          const isSnoozed = !!s && s > Date.now();
                          return (
                            <>
                              <div className="term-mail__row__menu-divider" />
                              <div className="term-mail__row__menu-group">
                                Snooze{isSnoozed ? ` · until ${formatSnoozeLabel(s as number)}` : ''}
                              </div>
                              {isSnoozed ? (
                                <button
                                  type="button"
                                  className="term-mail__row__menu-item"
                                  onClick={(e) => { e.stopPropagation(); void setSnooze(email, null); }}
                                >
                                  Unsnooze
                                </button>
                              ) : (
                                snoozePresets().map((p) => (
                                  <button
                                    key={p.label}
                                    type="button"
                                    className="term-mail__row__menu-item term-mail__row__menu-item--folder"
                                    onClick={(e) => { e.stopPropagation(); void setSnooze(email, p.ms); }}
                                  >
                                    {p.label}
                                  </button>
                                ))
                              )}
                            </>
                          );
                        })()}

                        {folderNav.leaves.length > 0 && (
                          <>
                            <div className="term-mail__row__menu-divider" />
                            <div className="term-mail__row__menu-group">Move to Folder</div>
                            {isFiledRow && matchVirtualFolderByOutlookName(email, customFolders) && (
                              // Name-routed filed mail cannot be moved by a sender
                              // rule (the Outlook folder wins), so don't imply it can.
                              <div className="term-mail__row__menu-note">
                                filed in Outlook: {email.outlookFolderPath || email.outlookFolderName}
                                {' - picking a folder only routes future mail from this sender'}
                              </div>
                            )}
                            {folderNav.leaves.map((cf) => (
                              <button
                                key={cf.id}
                                type="button"
                                className={
                                  'term-mail__row__menu-item term-mail__row__menu-item--folder'
                                  + (email.virtualFolderId === cf.id
                                      ? ' term-mail__row__menu-item--active'
                                      : '')
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssignSender(cf.id, email);
                                }}
                              >
                                {email.virtualFolderId === cf.id ? '✓ ' : '  '}{cf.name}
                              </button>
                            ))}
                          </>
                        )}

                        {/* Link to Job — auto-suggest active jobs for the
                            matched customer, plus search + create options. */}
                        {(() => {
                          const trueCid    = getTrueCustomerId(email);
                          const matchName  = trueCid ? customerNameById.get(trueCid) || null : null;
                          const suggested  = trueCid ? getJobsForCustomer(trueCid) : [];
                          const linkedId   = (email as any).linkedJobId as string | undefined;
                          const linkedLbl  = (email as any).linkedJobLabel as string | undefined;
                          const isBusy     = linkingEmailId === (email.emailId || email.id);
                          return (
                            <>
                              <div className="term-mail__row__menu-divider" />
                              <div className="term-mail__row__menu-group">
                                Link to Job{matchName ? ` · ${matchName}` : ''}
                              </div>
                              {linkedId && (
                                <div className="term-mail__row__menu-note">
                                  ✓ linked: {linkedLbl || linkedId}
                                </div>
                              )}
                              {suggested.length === 0 && !linkedId && (
                                <div className="term-mail__row__menu-note">
                                  {trueCid ? 'no active jobs for this customer' : 'no matched customer'}
                                </div>
                              )}
                              {suggested.map((job) => {
                                const isActive = linkedId === job.id;
                                return (
                                  <button
                                    key={job.id}
                                    type="button"
                                    className={
                                      'term-mail__row__menu-item term-mail__row__menu-item--folder'
                                      + (isActive ? ' term-mail__row__menu-item--active' : '')
                                    }
                                    disabled={isBusy}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const eid = email.emailId || email.id;
                                      if (eid) handleLinkToJob(eid, job.id, job.jobLabel);
                                    }}
                                  >
                                    {isActive ? '✓ ' : '  '}{job.jobLabel}
                                    <span className="term-mail__row__menu-tail">{job.status}</span>
                                  </button>
                                );
                              })}
                              <button
                                type="button"
                                className="term-mail__row__menu-item term-mail__row__menu-item--folder"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openLinkModal(email);
                                }}
                              >
                                  Search other customer...
                              </button>
                              <button
                                type="button"
                                className="term-mail__row__menu-item term-mail__row__menu-item--folder"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCreateJobModal(email, trueCid);
                                }}
                              >
                                  Create new job...
                              </button>
                            </>
                          );
                        })()}

                        {/* Delete = a real move to Deleted Items with an undo toast, so no arm-and-confirm.
                            Inside Deleted the same slot is "delete forever" (it asks first).
                            Sent items and drafts cannot be moved, so they get neither. */}
                        {f === 'deleted' ? (
                          <>
                            <div className="term-mail__row__menu-divider" />
                            <button
                              type="button"
                              className="term-mail__row__menu-item term-mail__row__menu-item--danger"
                              onClick={(e) => { e.stopPropagation(); setActionMenuFor(null); void purgeRow(email); }}
                            >
                              Delete forever...
                            </button>
                          </>
                        ) : isMovableEmail(email) ? (
                          <>
                            <div className="term-mail__row__menu-divider" />
                            <button
                              type="button"
                              className="term-mail__row__menu-item term-mail__row__menu-item--danger"
                              onClick={(e) => { e.stopPropagation(); setActionMenuFor(null); void deleteRow(email); }}
                            >
                              Delete{threadCount ? ` conversation (${threadCount})` : ''}
                            </button>
                          </>
                        ) : null}
                      </div>
                    )}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Footer — load more / done */}
        {toRender.length > 0 && (
          <div className="term-mail__footer">
            {isLoadingOlderEmails ? (
              <span>loading older messages...</span>
            ) : hasMoreToShow ? (
              <button type="button" className="term-mail__action" onClick={handleLoadMore}>
                {br('load more')}
              </button>
            ) : (
              <span>— end of mailbox —</span>
            )}
          </div>
        )}

        {/* Bulk action bar: a flex item under the list (never over a row). On a phone it steps
            aside while the search box has the keyboard - it would sit behind it. */}
        {selecting && activeFolder !== 'outbox' && !(isNarrow && searchFocused) && (
          <BulkActionBar
            count={selectionInfo.rows}
            messageCount={selectedIds.size}
            busyLabel={bulkBusy || (isMarkingAll ? 'marking read...' : null)}
            canMarkRead={selectionInfo.canMarkRead}
            // Already-there moves are pointless in their own view; while searching, rows
            // come from every folder, so every action stays on (planMoves skips the no-ops).
            showArchive={isSearching || activeFolder !== 'archive'}
            showDelete={isSearching || activeFolder !== 'deleted'}
            showInbox={isSearching || activeFolder === 'archive' || activeFolder === 'deleted' || activeFolder === 'junk'}
            allSelected={selectionInfo.allRows}
            onMarkRead={bulkMarkRead}
            onArchive={() => bulkMove('archive')}
            onDelete={() => bulkMove('deleted')}
            onMoveToInbox={() => bulkMove('inbox')}
            onSelectAll={selectAllRows}
            onClear={clearSelection}
            br={br}
          />
        )}
      </div>

      {/* ── Customer/Job search modal (Link to Job → "Search other customer…") ── */}
      <Modal
        show={linkModalOpen}
        onHide={() => { setLinkModalOpen(false); setLinkModalEmail(null); setLinkModalSelectedCustomerId(null); setLinkModalSearch(''); }}
        centered
        size="lg"
        contentClassName="term-mail__link-modal"
        dialogClassName="term-mail__link-modal-dialog"
      >
        <div className="term-mail__link-modal__head">
          <span>$ link email → job</span>
          <button
            type="button"
            className="term-mail__link-modal__close"
            onClick={() => { setLinkModalOpen(false); setLinkModalEmail(null); setLinkModalSelectedCustomerId(null); setLinkModalSearch(''); }}
            aria-label="Close"
          >{br('×')}</button>
        </div>
        <div className="term-mail__link-modal__body">
          {!linkModalSelectedCustomerId ? (
            <>
              <div className="term-mail__link-modal__label">customer:</div>
              <input
                type="text"
                className="term-mail__link-modal__input"
                value={linkModalSearch}
                onChange={(e) => setLinkModalSearch(e.target.value)}
                placeholder="search customers by name, company, or email..."
                autoFocus
              />
              <div className="term-mail__link-modal__list">
                {linkModalCustomerOptions.length === 0 ? (
                  <div className="term-mail__link-modal__empty">no customers match.</div>
                ) : linkModalCustomerOptions.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="term-mail__link-modal__row"
                    onClick={() => setLinkModalSelectedCustomerId(c.id || null)}
                  >
                    <span className="term-mail__link-modal__row-name">{c.name || '(unnamed)'}</span>
                    {c.company && <span className="term-mail__link-modal__row-meta">{c.company}</span>}
                    {c.email && <span className="term-mail__link-modal__row-meta">{c.email}</span>}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="term-mail__link-modal__label">
                customer: <strong>{customerNameById.get(linkModalSelectedCustomerId) || '(unknown)'}</strong>
                <button
                  type="button"
                  className="term-mail__link-modal__back"
                  onClick={() => setLinkModalSelectedCustomerId(null)}
                >{br('change')}</button>
              </div>
              <div className="term-mail__link-modal__label">jobs:</div>
              <div className="term-mail__link-modal__list">
                {linkModalJobs.length === 0 ? (
                  <div className="term-mail__link-modal__empty">no jobs for this customer.</div>
                ) : linkModalJobs.map((j) => (
                  <button
                    key={j.id}
                    type="button"
                    className="term-mail__link-modal__row"
                    disabled={!!linkingEmailId}
                    onClick={() => {
                      if (!linkModalEmail) return;
                      const eid = linkModalEmail.emailId || linkModalEmail.id;
                      if (eid) handleLinkToJob(eid, j.id, j.jobLabel);
                    }}
                  >
                    <span className="term-mail__link-modal__row-name">{j.jobLabel}</span>
                    <span className="term-mail__link-modal__row-meta">{j.status}</span>
                  </button>
                ))}
              </div>
              <div className="term-mail__link-modal__footer-actions">
                <button
                  type="button"
                  className="term-mail__action"
                  onClick={() => {
                    if (!linkModalEmail) return;
                    openCreateJobModal(linkModalEmail, linkModalSelectedCustomerId);
                  }}
                >
                  [+ create new job for this customer]
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* ── Terminal-native NewJobModal. Same data contract as the
            default theme's NewJobModal, no Bootstrap-form chrome. On
            success the email is linked to the new job. ── */}
      <TerminalNewJobModal
        show={showNewJobModal}
        onClose={() => {
          setShowNewJobModal(false);
          setNewJobEmailContext(null);
        }}
        onJobCreated={handleNewJobCreated}
        initialCustomerId={newJobEmailContext?.customerId || undefined}
        initialDescription={newJobEmailContext?.email?.title || ''}
      />
    </div>
  );
};

export default TerminalEmailFeed;
